<template>
  <div class="modal-overlay" v-if="isOpen" @click.self="close">
    <div class="modal-container">
      <div class="modal-sidebar" :class="{ 'agent-mode': isAgentMode }">
        <div class="modal-header">
          <h3>{{ $t('settingsModal.title') }}</h3>
          <div class="modal-close-btn" @click="close">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </div>
        </div>
        <ul class="settings-nav">
          <li :class="{ active: activeSettingTab === 'general' }" @click="activeSettingTab = 'general'">
            <span>{{ $t('settingsModal.tabs.general') }}</span>
          </li>
          <li :class="{ active: activeSettingTab === 'model' }" @click="activeSettingTab = 'model'">
            <span>{{ $t('settingsModal.tabs.model') }}</span>
          </li>
          <li :class="{ active: activeSettingTab === 'appearance' }" @click="activeSettingTab = 'appearance'">
            <span>{{ $t('settingsModal.tabs.appearance') }}</span>
          </li>
          <li :class="{ active: activeSettingTab === 'memory' }" @click="openMemoryTab">
            <span>{{ $t('settingsModal.tabs.memory') }}</span>
          </li>
          <li :class="{ active: activeSettingTab === 'backup' }" @click="activeSettingTab = 'backup'">
            <span>{{ $t('settingsModal.tabs.backup') }}</span>
          </li>
          <li :class="{ active: activeSettingTab === 'about' }" @click="activeSettingTab = 'about'">
            <span>{{ $t('settingsModal.tabs.about') }}</span>
          </li>
        </ul>
      </div>
      <div class="modal-content">
        <div v-if="activeSettingTab === 'general'" class="settings-panel">
          <h4>{{ $t('settingsModal.generalTitle') }}</h4>
          <div class="form-group">
            <label>{{ $t('settingsModal.theme') }}</label>
            <select :value="theme" @change="setTheme($event.target.value)">
              <option value="light">{{ $t('settingsModal.themeLight') }}</option>
              <option value="dark">{{ $t('settingsModal.themeDark') }}</option>
            </select>
          </div>
        </div>

        <div v-if="activeSettingTab === 'appearance'" class="settings-panel">
          <h4>{{ $t('settingsModal.appearanceTitle') }}</h4>
          <div class="appearance-card">
            <div class="appearance-header">
               <div class="appearance-info">
                  <span class="appearance-label">{{ $t('settingsModal.fontSizeLabel') }}</span>
                  <span class="appearance-value">{{ localSettings.fontSize }}px</span>
               </div>
               <div class="slider-wrapper">
                  <input type="range" min="12" max="24" step="1" 
                         v-model.number="localSettings.fontSize" 
                         class="modern-slider">
               </div>
            </div>
            
            <div >
              <div class="preview-bubble" :style="{ fontSize: localSettings.fontSize + 'px' }">
                <span class="bubble-icon">✨</span>
                <span class="bubble-text">{{ $t('settingsModal.fontPreview') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeSettingTab === 'memory'" class="settings-panel memory-settings-panel">
          <div class="memory-page-heading">
            <div>
              <h4>{{ $t('settingsModal.memory.title') }}</h4>
              <p>{{ $t('settingsModal.memory.subtitle') }}</p>
            </div>
            <span class="memory-local-badge">{{ $t('settingsModal.memory.localBadge') }}</span>
          </div>

          <div class="memory-privacy-notice" role="note">
            <span class="memory-privacy-icon" aria-hidden="true">⌂</span>
            <div>
              <strong>{{ $t('settingsModal.memory.privacyTitle') }}</strong>
              <p>{{ $t('settingsModal.memory.privacyDesc') }}</p>
            </div>
          </div>

          <section class="memory-card">
            <div class="memory-card-heading">
              <div><h5>{{ $t('settingsModal.memory.soulTitle') }}</h5><p>{{ $t('settingsModal.memory.soulDesc') }}</p></div>
              <button v-if="!memorySoulEditing" type="button" class="btn-secondary memory-action" @click="memorySoulEditing = true">{{ $t('settingsModal.memory.edit') }}</button>
            </div>
            <textarea v-if="memorySoulEditing" v-model="memorySoulDraft" class="memory-textarea" rows="5" :placeholder="$t('settingsModal.memory.soulPlaceholder')"></textarea>
            <p v-else class="memory-content-preview">{{ memorySoulText || $t('settingsModal.memory.emptyContent') }}</p>
            <div v-if="memorySoulEditing" class="memory-editor-actions">
              <button type="button" class="btn-secondary memory-action" @click="cancelMemoryEdit('soul')">{{ $t('settingsModal.memory.cancel') }}</button>
              <button type="button" class="btn-primary memory-action" @click="saveMemorySection('soul')">{{ $t('settingsModal.memory.save') }}</button>
            </div>
          </section>

          <section class="memory-card">
            <div class="memory-card-heading">
              <div><h5>{{ $t('settingsModal.memory.profileTitle') }}</h5><p>{{ $t('settingsModal.memory.profileDesc') }}</p></div>
              <button v-if="!memoryProfileEditing" type="button" class="btn-secondary memory-action" @click="memoryProfileEditing = true">{{ $t('settingsModal.memory.edit') }}</button>
            </div>
            <textarea v-if="memoryProfileEditing" v-model="memoryProfileDraft" class="memory-textarea" rows="5" :placeholder="$t('settingsModal.memory.profilePlaceholder')"></textarea>
            <p v-else class="memory-content-preview">{{ memoryProfileText || $t('settingsModal.memory.emptyContent') }}</p>
            <div v-if="memoryProfileEditing" class="memory-editor-actions">
              <button type="button" class="btn-secondary memory-action" @click="cancelMemoryEdit('profile')">{{ $t('settingsModal.memory.cancel') }}</button>
              <button type="button" class="btn-primary memory-action" @click="saveMemorySection('profile')">{{ $t('settingsModal.memory.save') }}</button>
            </div>
          </section>

          <section class="memory-card">
            <div class="memory-card-heading">
              <div><h5>{{ $t('settingsModal.memory.longTermTitle') }}</h5><p>{{ $t('settingsModal.memory.longTermDesc') }}</p></div>
              <span class="memory-count">{{ $t('settingsModal.memory.itemCount', { count: memoryItems.length }) }}</span>
            </div>
            <div v-if="memoryItems.length" class="memory-item-list">
              <div v-for="item in memoryItems" :key="item.id" class="memory-item">
                <template v-if="memoryItemEditingId === item.id">
                  <textarea v-model="memoryItemDraft" class="memory-textarea" rows="3"></textarea>
                  <div class="memory-editor-actions">
                    <button type="button" class="btn-secondary memory-action" @click="cancelMemoryItemEdit">{{ $t('settingsModal.memory.cancel') }}</button>
                    <button type="button" class="btn-primary memory-action" @click="saveMemoryItem(item)">{{ $t('settingsModal.memory.save') }}</button>
                  </div>
                </template>
                <template v-else>
                  <small v-if="item.label" class="memory-item-label">{{ item.label }}</small>
                  <p class="memory-item-content">{{ formatMemoryItem(item.value) }}</p>
                  <div class="memory-item-actions">
                    <button type="button" class="memory-text-button" @click="editMemoryItem(item)">{{ $t('settingsModal.memory.edit') }}</button>
                    <button type="button" class="memory-text-button danger" @click="deleteMemoryItem(item)">{{ $t('settingsModal.memory.delete') }}</button>
                  </div>
                </template>
              </div>
            </div>
            <p v-else class="memory-empty-state">{{ $t('settingsModal.memory.emptyList') }}</p>
            <div v-if="memoryItemAdding" class="memory-add-editor">
              <textarea v-model="memoryItemDraft" class="memory-textarea" rows="3" :placeholder="$t('settingsModal.memory.itemPlaceholder')"></textarea>
              <div class="memory-editor-actions">
                <button type="button" class="btn-secondary memory-action" @click="cancelMemoryItemEdit">{{ $t('settingsModal.memory.cancel') }}</button>
                <button type="button" class="btn-primary memory-action" @click="addMemoryItem">{{ $t('settingsModal.memory.save') }}</button>
              </div>
            </div>
            <button v-else type="button" class="memory-add-button" @click="startAddMemoryItem">＋ {{ $t('settingsModal.memory.addItem') }}</button>
          </section>

          <div class="memory-clear-row">
            <div><strong>{{ $t('settingsModal.memory.clearTitle') }}</strong><p>{{ $t('settingsModal.memory.clearDesc') }}</p></div>
            <button type="button" class="btn-danger memory-action" @click="clearMemoryData">{{ $t('settingsModal.memory.clearButton') }}</button>
          </div>
        </div>

        <div v-if="activeSettingTab === 'model'" class="settings-panel">
          <div class="settings-header compact-header">
            <h4 class="panel-main-title">{{ $t('settingsModal.modelManagement') }}</h4>
            <div v-if="!editingModelId" class="model-header-actions">
              <div class="btn-secondary action-div-btn add-btn-pill" @click="openTokenStore">
                {{ $t('settingsModal.tokenStore') }}
              </div>
              <div class="btn-primary action-div-btn add-btn-pill" @click="startAddModel">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                {{ $t('settingsModal.add') }}
              </div>
            </div>
          </div>
          
          <!-- Model Dashboard -->
          <div v-if="!editingModelId && !showTokenStore" class="model-dashboard compact-dashboard">
            <section
              v-for="category in modelCategories"
              :key="category.id"
              class="model-section"
              :class="{ 'is-collapsed': !isCategoryExpanded(category.id) }"
            >
               <button
                 type="button"
                 class="model-section-header"
                 @click="toggleModelCategory(category.id)"
                 :aria-expanded="isCategoryExpanded(category.id).toString()"
               >
                 <div class="model-section-title">
                   <span class="model-section-icon">{{ category.icon }}</span>
                   <span class="model-section-copy">
                     <strong>{{ category.name }}</strong>
                     <small>{{ getActiveModelByType(category.id) ? getActiveModelByType(category.id).name : $t('settingsModal.emptyConfig') }}</small>
                   </span>
                 </div>
                 <div class="model-section-meta">
                   <span class="model-count-pill">{{ getModelsByType(category.id).length }}</span>
                   <svg class="collapse-chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                     <polyline points="6 9 12 15 18 9"></polyline>
                   </svg>
                 </div>
               </button>
               
                <div v-show="isCategoryExpanded(category.id)" class="model-section-body">
                  <div v-if="getModelsByType(category.id).length === 0" class="simple-empty-state">
                    <template v-if="category.id === 'text' && isAgentMode">
                      <span class="text-sm">
                        {{ $t('settingsModal.builtinModelHintPrefix') }}<a href="#" @click.prevent="startAddModel">{{ $t('settingsModal.addModelLink') }}</a>{{ $t('settingsModal.builtinModelHintSuffix') }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-sm">{{ $t('settingsModal.emptyConfig') }}</span>
                    </template>
                  </div>
                 
                 <div class="simple-model-list" v-else>
                   <div v-for="model in getModelsByType(category.id)"
                        :key="model.id" 
                        class="simple-model-row"
                        draggable="true"
                        :class="{ 'is-active': isModelActive(model.id, category.id), 'is-drag-over': dragOverManagementModelId === model.id }"
                        @dragstart="startManagementModelDrag(model, $event)"
                        @dragover.prevent="dragOverManagementModel(model, $event)"
                        @drop.prevent="dropManagementModel(model, category.id)"
                        @click="selectModelByType(model.id, category.id)">

                     <span class="simple-model-drag-handle" :title="$t('settingsModal.dragToSort')" aria-hidden="true">
                       <svg viewBox="0 0 18 14" width="18" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                         <line x1="1" y1="2" x2="17" y2="2"></line>
                         <line x1="1" y1="7" x2="17" y2="7"></line>
                         <line x1="1" y1="12" x2="17" y2="12"></line>
                       </svg>
                     </span>

                     <div class="simple-model-main">
                        <div class="simple-model-title">
                          <span>{{ model.name }}</span>
                          <span v-if="isModelActive(model.id, category.id)" class="simple-active-label">{{ $t('settingsModal.activeModel') }}</span>
                        </div>
                        <div class="simple-model-subtitle">{{ model.model }}</div>
                        <div v-if="model.tokenStore" class="model-balance">
                          {{ formatTokenStoreBalance(model) }}
                        </div>
                      </div>
                     
                     <div class="simple-model-actions">
                        <div class="simple-model-more" @click.stop>
                          <button type="button" class="simple-more-btn" :aria-expanded="(managementModelMenuId === model.id).toString()" :title="$t('settingsModal.moreActions')" @click.stop="toggleManagementModelMenu(model.id)">
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                              <circle cx="5" cy="12" r="1.7"></circle>
                              <circle cx="12" cy="12" r="1.7"></circle>
                              <circle cx="19" cy="12" r="1.7"></circle>
                            </svg>
                          </button>
                          <div v-if="managementModelMenuId === model.id" class="simple-model-menu" @click.stop>
                            <button type="button" class="simple-model-menu-item" @click="toggleManagementModelPinned(model); closeManagementModelMenu()">
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3 2.3 5.2 5.7.6-4.2 3.8 1.2 5.6L12 15.2 7 18.2l1.2-5.6L4 8.8l5.7-.6L12 3Z"></path></svg>
                              <span>{{ model.pinned ? $t('settingsModal.unpinModel') : $t('settingsModal.pinModel') }}</span>
                            </button>
                            <button v-if="model.id !== 'default-agent'" type="button" class="simple-model-menu-item" @click="startEditModel(model); closeManagementModelMenu()">
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"></path></svg>
                              <span>{{ $t('settingsModal.edit') }}</span>
                            </button>
                            <button v-if="model.id !== 'default-agent'" type="button" class="simple-model-menu-item danger" @click="confirmDeleteModel(model.id); closeManagementModelMenu()" :disabled="store.settings.models.length <= 1">
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"></path><path d="M10 11v6M14 11v6"></path><path d="m6 7 1 13h10l1-13M9 7V4h6v3"></path></svg>
                              <span>{{ $t('settingsModal.delete') }}</span>
                            </button>
                          </div>
                        </div>
                     </div>
                   </div>
                 </div>
               </div>
            </section>
          </div>

          <TokenStoreFullscreen
            v-if="showTokenStore"
            @close="closeTokenStore"
            @model-added="refreshTokenStoreBalances"
          />

          <!-- Model Editor Form -->
          <div v-if="editingModelId" class="compact-editor">
            <div class="editor-header-compact">
               <h5>{{ editingModelId === 'new' ? $t('settingsModal.addModel') : $t('settingsModal.editModel') }}</h5>
               <button class="icon-btn" @click="cancelEditModel" :title="$t('settingsModal.back')">
                 <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
               </button>
             </div>
             
             <div class="compact-form">
                 <div class="form-row">
                   <div class="form-group-compact">
                    <label>{{ $t('settingsModal.provider') }} <a v-if="selectedProviderCourse" :href="selectedProviderCourse" target="_blank" class="course-link">{{ $t('settingsModal.tutorial') }}</a></label>
                     <select v-model="modelEditForm.provider" @change="handleProviderChange" class="compact-input">
                       <option v-for="(p, index) in providers" :key="index" :value="p.id">{{ p.name }}</option>
                     </select>
                   </div>
                   
                   <div class="form-group-compact">
                    <label>{{ $t('settingsModal.modelType') }}</label>
                     <div class="type-selector-compact">
                       <div v-for="t in filteredModelTypes" :key="t.id" 
                            class="type-option-compact" 
                           :class="{ active: modelEditForm.type === t.id }"
                           @click="modelEditForm.type = t.id; handleTypeChange()">
                        {{ t.name }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-row model-name-row">
                  <div class="form-group-compact model-config-name-field">
                    <label>{{ $t('settingsModal.configName') }}</label>
                    <input type="text" v-model="modelEditForm.name" :placeholder="$t('settingsModal.configNamePlaceholder')" class="compact-input">
                  </div>
                </div>

                <div class="form-group-compact model-identifier-field">
                    <div class="field-label-row">
                      <label>{{ $t('settingsModal.modelIdentifier') }}</label>
                      <span
                        class="inline-fetch-action"
                        :class="{ disabled: modelListLoading }"
                        role="button"
                        tabindex="0"
                        @click="fetchModelList"
                        @keydown.enter.prevent="fetchModelList"
                        @keydown.space.prevent="fetchModelList"
                      >
                        {{ modelListLoading ? $t('settingsModal.fetchingModels') : $t('settingsModal.fetchModels') }}
                      </span>
                    </div>
                    <div ref="modelCombobox" class="model-picker">
                      <template v-if="pickerModels.length">
                        <div class="model-picker-toolbar">
                          <div class="model-custom-input-wrap">
                            <input
                              v-model.trim="modelSearchQuery"
                              type="text"
                              class="compact-input model-picker-search"
                              :placeholder="$t('settingsModal.customModelPlaceholder')"
                              @keydown.enter.prevent="addCustomModel"
                            >
                            <button v-if="canAddCustomModel" type="button" class="model-custom-add" :title="$t('settingsModal.addCustomModel')" @click="addCustomModel">+</button>
                          </div>
                          <button type="button" class="model-picker-select-all" @click="toggleSelectAllFetchedModels">
                            {{ allFetchedModelsSelected ? $t('settingsModal.clearAllModels') : $t('settingsModal.selectAllModels') }}
                          </button>
                        </div>
                        <div class="model-picker-summary">
                          {{ $t('settingsModal.selectedModelsCount', { selected: selectedFetchedModels.length, total: pickerModels.length }) }}
                        </div>
                        <div class="model-picker-section">
                          <div class="model-picker-section-title"><span>{{ $t('settingsModal.allModels') }}</span><small>{{ filteredPickerModels.length }}</small></div>
                          <div class="model-capsule-cloud">
                            <button
                              v-for="item in filteredPickerModels"
                              :key="item.id"
                              type="button"
                              class="model-capsule"
                              :class="{ selected: isFetchedModelSelected(item.id) }"
                              :title="item.displayName || item.ownedBy || item.id"
                              @click="toggleFetchedModel(item)"
                            >
                              <span>{{ item.id }}</span>
                            </button>
                            <div v-if="!filteredAvailableModels.length" class="custom-combobox-empty">{{ $t('settingsModal.noMatchingModels') }}</div>
                          </div>
                        </div>
                        <div class="model-picker-section selected-models-section">
                          <div class="model-picker-section-title"><span>{{ $t('settingsModal.selectedModels') }}</span><small>{{ selectedModelOptions.length }}</small></div>
                          <div v-if="selectedModelOptions.length" class="model-capsule-cloud selected-model-capsule-cloud">
                            <div v-for="item in selectedModelOptions" :key="item.id" class="model-capsule selected" :class="{ pinned: item.pinned, 'is-drag-over': dragOverSelectedModelId === item.id }" draggable="true" @dragstart="startSelectedModelDrag(item, $event)" @dragover.prevent="dragOverSelectedModel(item, $event)" @drop.prevent="dropSelectedModel(item)">
                              <span class="model-capsule-drag-handle" :title="$t('settingsModal.dragToSort')" aria-hidden="true"><svg viewBox="0 0 18 14" width="16" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="2" x2="17" y2="2"></line><line x1="1" y1="7" x2="17" y2="7"></line><line x1="1" y1="12" x2="17" y2="12"></line></svg></span><span class="model-capsule-label">{{ item.id }}</span>
                              <button type="button" class="model-capsule-pin" :class="{ pinned: item.pinned }" :title="item.pinned ? $t('settingsModal.unpinModel') : $t('settingsModal.pinModel')" @click.stop="toggleSelectedModelPinned(item)">{{ item.pinned ? '★' : '☆' }}</button>
                              <button type="button" class="model-capsule-remove" :title="$t('settingsModal.removeSelectedModel')" @click.stop="toggleFetchedModel(item)"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line></svg></button>
                            </div>
                          </div>
                          <div v-else class="model-picker-empty-selected">{{ $t('settingsModal.noSelectedModels') }}</div>
                        </div>
                      </template>
                      <div v-else class="model-custom-input-wrap standalone-custom-model-input">
                        <input
                          type="text"
                          v-model.trim="modelSearchQuery"
                          :placeholder="$t('settingsModal.customModelPlaceholder')"
                          class="compact-input custom-combobox-field"
                          @keydown.enter.prevent="addCustomModel"
                        >
                        <button v-if="canAddCustomModel" type="button" class="model-custom-add" :title="$t('settingsModal.addCustomModel')" @click="addCustomModel">+</button>
                      </div>
                    </div>
                    <div v-if="modelListError" class="model-fetch-feedback error">{{ modelListError }}</div>
                    <div v-else-if="modelListHint" class="model-fetch-feedback">
                      {{ modelListHint }}
                      <span v-if="lastModelCatalogSource"> ({{ lastModelCatalogSource }})</span>
                    </div>
                </div>

                <div class="form-group-compact">
                  <label>{{ $t('settingsModal.endpoint') }}</label>
                  <input type="text" v-model="modelEditForm.endpoint" placeholder="https://api.openai.com/v1/chat/completions" class="compact-input">
                </div>
                
                <div class="form-group-compact">
                  <label>{{ $t('settingsModal.apiKey') }}</label>
                  <input v-model="modelEditForm.apiKey" placeholder="sk-..." class="compact-input">
                </div>
            </div>
            
            <div class="editor-footer-compact">
              <button class="btn-primary btn-small btn-block" @click="saveModel">{{ $t('settingsModal.saveConfig') }}</button>
            </div>
          </div>
        </div>

        <div v-if="activeSettingTab === 'backup'" class="tab-content pb-6 space-y-6">
            <h4>{{ $t('settingsModal.backupTitle') }}</h4>
          <!-- 账号登录 -->
          <div class="bg-card rounded-[24px] shadow-card p-5 flex items-center justify-between mb-4">
            <div class="flex items-center space-x-4">
              <div v-if="!isLoggedIn" class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <img v-else :src="userAvatar" :alt="$t('settingsModal.avatarAlt')" class="w-12 h-12 rounded-full object-cover border-2 border-gray-100 flex-shrink-0">
               
              <div>
                <h3 class="text-[16px] font-semibold m-0">{{ isLoggedIn ? userName : $t('settingsModal.notLoggedIn') }}</h3>
                <p class="text-[13px] text-textSub mt-0.5 m-0">{{ isLoggedIn ? (userEmail || $t('settingsModal.loggedInCloudSync')) : $t('settingsModal.loginToEnableCloudSync') }}</p>
              </div>
            </div>
            
            <div v-if="!isLoggedIn">
              <button class="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full active:scale-95 transition-transform shadow-md shadow-primary/30" @click="handleLoginClick">
                {{ $t('settingsModal.login') }}
              </button>
            </div>
            <div v-else class="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full active:scale-95 hover:bg-gray-200 transition-all cursor-pointer" @click="logout">
              {{ $t('settingsModal.logout') }}
            </div>
          </div>

          <!-- 同步与提醒 -->
          <div class="bg-card rounded-[24px] shadow-card overflow-hidden mb-4">
            <!-- 云端同步 -->
            <div v-if="isLoggedIn" class="px-5 py-4 flex items-center justify-between border-b border-gray-100">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-xl bg-blue-50 text-primary flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
                  </svg>
                </div>
                <span class="text-[15px] font-medium leading-none">{{ $t('settingsModal.cloudSyncEnabled') }}</span>
              </div>
              
              <!-- Toggle -->
              <div class="flex items-center space-x-3">
                <div v-if="isLoggedIn && store.settings.cloudSyncEnabled" 
                  class="mr-2 px-3 py-1 bg-primary/10 text-primary text-[12px] font-medium rounded-full hover:bg-primary/20 active:scale-95 transition-all flex items-center space-x-1 cursor-pointer" 
                  :class="{ 'opacity-50 pointer-events-none': cloudLoading }"
                  @click="uploadCloudBackup">
                  <svg v-if="cloudLoading" class="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
                  <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                  <span>{{ $t(cloudLoading ? 'settingsModal.syncing' : 'settingsModal.syncNow') }}</span>
                </div>
                <div class="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                  <input type="checkbox" name="toggle" id="sync-toggle" class="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer z-10 transition-all duration-300 shadow-sm" :checked="store.settings.cloudSyncEnabled" @change="toggleCloudSync"/>
                  <label for="sync-toggle" class="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer transition-colors duration-300" :class="{ 'bg-primary': store.settings.cloudSyncEnabled }"></label>
                </div>
              </div>
            </div>
            
            <!-- 提醒天数 -->
            <div class="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 transition-colors" @click="promptBackupDays">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-xl bg-yellow-50 text-yellow-600 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </div>
                <span class="text-[15px] font-medium leading-none">{{ $t('settingsModal.backupReminderDays') }}</span>
              </div>
              <div class="flex items-center space-x-2 text-textSub">
                <span class="text-sm leading-none">{{ $t('settingsModal.daysValue', { days: store.settings.backupReminderDays }) }}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </div>
            </div>
          </div>

          <section class="bg-card rounded-[24px] shadow-card overflow-hidden mb-4 local-session-backups">
            <div class="px-5 py-4 border-b border-gray-100">
              <h5 class="m-0 text-[15px] font-semibold">{{ $t('settingsModal.localSessionBackups.title') }}</h5>
              <p class="m-0 mt-1 text-[12px] text-textSub">{{ $t('settingsModal.localSessionBackups.description') }}</p>
            </div>
            <p v-if="sessionBackupLoading" class="px-5 py-4 text-sm text-textSub">{{ $t('settingsModal.localSessionBackups.loading') }}</p>
            <p v-else-if="sessionBackupError" class="px-5 py-4 text-sm text-red-500">{{ $t('settingsModal.localSessionBackups.loadFailed', { message: sessionBackupError }) }}</p>
            <p v-else-if="!sessionBackups.length" class="px-5 py-4 text-sm text-textSub">{{ $t('settingsModal.localSessionBackups.empty') }}</p>
            <div v-else>
              <div v-for="backup in sessionBackups" :key="backup.date" class="px-5 py-4 border-b border-gray-100 local-session-backup-row">
                <div class="local-session-backup-info">
                  <strong>{{ backup.date }}</strong>
                  <span>{{ $t('settingsModal.localSessionBackups.counts', { sessions: backup.sessionCount, messages: backup.messageCount }) }}</span>
                </div>
                <div class="local-session-backup-actions">
                  <button type="button" class="memory-text-button" :disabled="sessionBackupBusy" @click="viewSessionBackup(backup.date)">{{ $t('settingsModal.localSessionBackups.view') }}</button>
                  <button type="button" class="memory-text-button" :disabled="sessionBackupBusy" @click="restoreSessionBackup(backup.date)">{{ $t('settingsModal.localSessionBackups.restore') }}</button>
                </div>
              </div>
            </div>
            <div v-if="sessionBackupPreview" class="px-5 py-4 local-session-backup-preview">
              <div class="local-session-preview-heading">
                <strong>{{ $t('settingsModal.localSessionBackups.previewTitle', { date: sessionBackupPreview.date }) }}</strong>
                <button type="button" class="memory-text-button" @click="sessionBackupPreview = null">{{ $t('settingsModal.localSessionBackups.close') }}</button>
              </div>
              <p v-if="!sessionBackupPreview.sessions.length" class="text-sm text-textSub">{{ $t('settingsModal.localSessionBackups.empty') }}</p>
              <template v-else>
                <div class="local-session-preview-list">
                  <button v-for="session in sessionBackupPreview.sessions" :key="session.id" type="button" class="local-session-preview-item" :class="{ active: String(selectedBackupSessionId) === String(session.id) }" @click="selectedBackupSessionId = session.id">
                    <span>{{ session.title || $t('settingsModal.localSessionBackups.untitled') }}</span>
                    <small>{{ $t('settingsModal.localSessionBackups.sessionMessages', { count: (session.messages || []).length }) }}</small>
                  </button>
                </div>
                <div v-if="selectedBackupSession" class="local-session-transcript" aria-live="polite">
                  <div v-for="(message, index) in (selectedBackupSession.messages || [])" :key="`${selectedBackupSession.id}-${index}`" class="local-session-message">
                    <strong>{{ message.role || $t('settingsModal.localSessionBackups.message') }}</strong>
                    <pre>{{ formatBackupMessage(message.content) }}</pre>
                  </div>
                  <p v-if="!(selectedBackupSession.messages || []).length" class="text-sm text-textSub">{{ $t('settingsModal.localSessionBackups.noMessages') }}</p>
                </div>
              </template>
            </div>
          </section>

            <!-- 本地数据 -->
            <div class="bg-card rounded-[24px] shadow-card overflow-hidden mb-4">
              <div class="px-5 py-3 bg-gray-50/50 border-b border-gray-100 local-data-section-header">
              <span class="text-xs font-medium text-textSub">{{ $t('settingsModal.localDataManagement') }}</span>
              </div>
            
            <!-- 导入 -->
            <button class="w-full px-5 py-4 flex items-center justify-between border-b border-gray-100 active:bg-gray-50 transition-colors bg-transparent border-none text-left local-data-action-btn" @click="$refs.backupInput.click()">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-xl bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </div>
                <span class="text-[15px] font-medium leading-none">{{ $t('settingsModal.dataImport') }}</span>
              </div>
              <svg class="text-gray-300 local-data-action-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
            <input type="file" ref="backupInput" @change="importData" style="display: none" accept=".json">
            
            <!-- 导出 -->
            <button class="w-full px-5 py-4 flex items-center justify-between active:bg-gray-50 transition-colors bg-transparent border-none text-left local-data-action-btn" @click="exportData">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <span class="text-[15px] font-medium leading-none">{{ $t('settingsModal.dataExport') }}</span>
              </div>
              <svg class="text-gray-300 local-data-action-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>

          <!-- 危险区域 -->
          <div class="bg-card rounded-[24px] shadow-card overflow-hidden danger-zone-new">
            <div class="px-5 py-3 bg-red-50/50 border-b border-red-100">
              <span class="text-xs font-medium text-red-500">{{ $t('settingsModal.dangerZone') }}</span>
            </div>
            <button class="w-full px-5 py-4 flex items-center justify-between active:bg-red-50 transition-colors bg-transparent border-none text-left" @click="resetAllData">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-xl bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </div>
                <div class="flex flex-col">
                  <span class="text-[15px] font-medium text-red-600 leading-none mb-1">{{ $t('settingsModal.clearAllLocalData') }}</span>
                  <span class="text-[12px] text-red-400 leading-none">{{ $t('settingsModal.actionIrreversible') }}</span>
                </div>
              </div>
              <svg class="text-red-200" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>

        <div v-if="activeSettingTab === 'about'" class="settings-panel">
          <h4>{{ $t('settingsModal.aboutTitle') }}</h4>
          <div class="about-section">
            <p class="about-desc">{{ $t('settingsModal.aboutDescription') }}</p>
            
            <div class="link-card">
              <a href="https://github.com/Auj625197595/rainsee-ai" target="_blank" class="github-link">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                </svg>
                <span>{{ $t('settingsModal.githubRepo') }}</span>
              </a>
            </div>

            <div class="legal-links">
              <a href="javascript:void(0)" class="legal-link" @click="$emit('show-agreement', 'privacy')">{{ $t('settingsModal.privacyPolicy') }}</a>
              <span class="legal-divider">·</span>
              <a href="javascript:void(0)" class="legal-link" @click="$emit('show-agreement', 'terms')">{{ $t('settingsModal.termsOfService') }}</a>
            </div>

            <div class="version-info">
              <p>{{ $t('settingsModal.starSupport') }}</p>
              <small class="version-text">{{ $t('settingsModal.versionLabel') }} 1.0.0</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store, mutations } from '../store';
import { saveAs } from 'file-saver';
import providersData from '../config/providers.json';
import TokenStoreFullscreen from './TokenStoreFullscreen.vue';
import { BACKUP_API_URL } from '@/api/config';
import { requestModelCatalog, validateModelCatalogEndpoint, MODEL_CATALOG_VALIDATION_ERRORS } from '@/utils/modelCatalog';
import { showPrompt } from '@/utils/dialog';
import { sessionBackupDB } from '@/utils/sessionBackupDB';
import { memoryDB } from '@/utils/db';

const TOKEN_STORE_API_BASE = 'https://wm.ktllq.cn/Token';
const TOKEN_STORE_URL_MARKER_KEY = 'tokenstore';
const TOKEN_STORE_URL_MARKER_VALUE = '1';
const MODEL_CATALOG_CACHE_KEY = 'aihelp_model_catalog_cache';

export default {
  name: 'SettingsModal',
  components: {
    TokenStoreFullscreen
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    initialTab: {
      type: String,
      default: 'model'
    }
  },
  emits: ['close', 'open-login'],
  data() {
    return {
      activeSettingTab: 'model',
      editingModelId: null, // null = list, 'new' = add, ID = edit
      cloudLoading: false,
      modelEditForm: {
        name: '',
        provider: '',
        type: 'text',
        endpoint: '',
        apiKey: '',
        model: '',
        modelOptions: []
      },
      providers: providersData,
      showTokenStore: false,
      expandedModelCategories: {},
      tokenStoreBalances: {},
      tokenStoreUrlMarkerHadValue: false,
      tokenStoreUrlMarkerValues: null,
      modelListLoading: false,
      modelListError: '',
      modelListHint: '',
      availableModels: [],
      modelDropdownOpen: false,
      highlightedModelIndex: -1,
      selectedFetchedModel: '',
      selectedFetchedModels: [],
      modelSearchQuery: '',
      customModelOptions: [],
      lastModelCatalogSource: '',
      draggedManagementModelId: '',
      draggedSelectedModelId: '',
      dragOverManagementModelId: '',
      dragOverSelectedModelId: '',
      managementModelMenuId: '',
      localSettings: {
        endpoint: '',
        apiKey: '',
        model: '',
        thinkingEnabled: false,
        webSearchEnabled: false,
        imageGenEnabled: false,
        fontSize: 15
      },
      memorySoulEditing: false,
      memoryProfileEditing: false,
      memorySoulDraft: '',
      memoryProfileDraft: '',
      memoryItemEditingId: null,
      memoryItemAdding: false,
      memoryItemDraft: '',
      sessionBackups: [],
      sessionBackupLoading: false,
      sessionBackupBusy: false,
      sessionBackupError: '',
      sessionBackupPreview: null,
      selectedBackupSessionId: null
    };
  },
  computed: {
    store() {
      return store;
    },
    theme() {
      return store.theme;
    },
    modelTypes() {
      return [
        { id: 'text', name: this.$t('settingsModal.modelTypes.text') },
        { id: 't2i', name: this.$t('settingsModal.modelTypes.t2i') },
        { id: 'i2i', name: this.$t('settingsModal.modelTypes.i2i') }
      ];
    },
    filteredModelTypes() {
      if (!this.modelEditForm.provider) return this.modelTypes;
      const provider = this.providers.find(p => p.name === this.modelEditForm.provider || p.id === this.modelEditForm.provider);
      if (!provider) return this.modelTypes;
      return this.modelTypes.filter(t => !!provider.configs[t.id]);
    },
    selectedProviderCourse() {
      console.log("providers", this.providers);
      if (!this.modelEditForm.provider) return null;
      const provider = this.providers.find(p => p.name === this.modelEditForm.provider || p.id === this.modelEditForm.provider);
      return provider ? provider.course : null;
    },
    modelCategories() {
      return [
        { id: 'text', name: this.$t('settingsModal.modelCategories.text'), icon: '💬' },
        { id: 't2i', name: this.$t('settingsModal.modelCategories.t2i'), icon: '🎨' },
        { id: 'i2i', name: this.$t('settingsModal.modelCategories.i2i'), icon: '🖼️' }
      ];
    },
    isAgentMode() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('mode') === 'agent';
    },
    isLoggedIn() {
      return !!store.token;
    },
    userInfo() {
      return store.userInfo || {};
    },
    userAvatar() {
      return this.userInfo.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80";
    },
    userName() {
      if (!this.isLoggedIn) return this.$t('settingsModal.notLoggedIn');
      return this.userInfo.nickname || store.user.profile || 'User_9527';
    },
    userEmail() {
      return this.userInfo.email || '';
    },
    pickerModels() {
      const models = [...(Array.isArray(this.availableModels) ? this.availableModels : []), ...(Array.isArray(this.customModelOptions) ? this.customModelOptions : [])];
      const seen = new Set();
      return models.filter(item => item && item.id && !seen.has(item.id) && seen.add(item.id));
    },
    filteredPickerModels() {
      const models = this.pickerModels;
      const query = (this.modelSearchQuery || '').trim().toLowerCase();
      if (!query) return models;

      return models.filter(item => {
        const candidates = [
          item.id,
          item.label,
          item.displayName,
          item.ownedBy
        ];
        return candidates.some(value => String(value || '').toLowerCase().includes(query));
      });
    },
    filteredAvailableModels() {
      return this.filteredPickerModels;
    },
    selectedModelOptions() {
      return this.selectedFetchedModels
        .map(id => this.pickerModels.find(item => item.id === id))
        .filter(Boolean);
    },
    allFetchedModelsSelected() {
      return this.pickerModels.length > 0 && this.selectedFetchedModels.length === this.pickerModels.length;
    },
    canAddCustomModel() {
      const value = (this.modelSearchQuery || '').trim();
      return !!value && !this.selectedFetchedModels.includes(value);
    },
    memorySoulText() {
      return this.getMemorySectionText(store.soul, 'personality');
    },
    memoryProfileText() {
      return this.getMemorySectionText(store.user, 'profile');
    },
    memoryItems() {
      if (Array.isArray(store.memory)) {
        return store.memory.map((value, index) => ({ id: `array:${index}`, source: 'array', index, value }));
      }
      if (!store.memory || typeof store.memory !== 'object') {
        return store.memory == null || store.memory === '' ? [] : [{ id: 'scalar:memory', source: 'scalar', value: store.memory }];
      }

      const items = [];
      if (Array.isArray(store.memory.longTerm)) {
        store.memory.longTerm.forEach((value, index) => items.push({ id: `longTerm:${index}`, source: 'longTerm', index, value }));
      }
      Object.entries(store.memory).forEach(([key, value]) => {
        if (key !== 'longTerm' && value != null && value !== '') {
          items.push({ id: `field:${key}`, source: 'field', key, label: key, value });
        }
      });
      return items;
    },
    selectedBackupSession() {
      if (!this.sessionBackupPreview) return null;
      return this.sessionBackupPreview.sessions.find(session => String(session.id) === String(this.selectedBackupSessionId)) || null;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    this.restoreTokenStoreFromUrl();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    this.syncTokenStoreUrlMarker(false);
  },
  watch: {
    activeSettingTab(newVal) {
      if (newVal === 'backup') this.loadSessionBackups();
    },
    isOpen(newVal) {
      if (newVal) {
        // Reset editing state
        this.editingModelId = null;
        this.managementModelMenuId = '';
        this.resetFetchedModelOptions();
        // Load general settings
        this.localSettings = { ...store.settings };
        
        // Set initial tab if provided
        if (this.initialTab) {
          this.activeSettingTab = this.initialTab;
        } else {
          this.activeSettingTab = 'model';
        }
        if (this.activeSettingTab === 'memory') this.prepareMemoryEditors();
        if (this.activeSettingTab === 'backup') this.loadSessionBackups();
        this.refreshTokenStoreBalances();
      } else if (this.showTokenStore) {
        this.showTokenStore = false;
      }
      if (!newVal) this.managementModelMenuId = '';
    },
    showTokenStore(newVal) {
      this.syncTokenStoreUrlMarker(newVal);
    },
    'localSettings.fontSize'(newVal) {
      if (this.isOpen) {
        mutations.setSettings({ fontSize: newVal });
      }
    },
    'localSettings.thinkingEnabled'(newVal) {
      if (this.isOpen) {
        mutations.setSettings({ thinkingEnabled: newVal });
      }
    },
    'localSettings.webSearchEnabled'(newVal) {
      if (this.isOpen) {
        mutations.setSettings({ webSearchEnabled: newVal });
      }
    },
    'localSettings.imageGenEnabled'(newVal) {
      if (this.isOpen) {
        mutations.setSettings({ imageGenEnabled: newVal });
      }
    }
  },
  methods: {
    openMemoryTab() {
      this.activeSettingTab = 'memory';
      this.prepareMemoryEditors();
    },
    getMemorySectionText(value, preferredKey) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const preferred = value[preferredKey];
        if (typeof preferred === 'string') return preferred;
        return JSON.stringify(value, null, 2);
      }
      return value == null ? '' : String(value);
    },
    prepareMemoryEditors() {
      this.memorySoulDraft = this.memorySoulText;
      this.memoryProfileDraft = this.memoryProfileText;
      this.memorySoulEditing = false;
      this.memoryProfileEditing = false;
      this.cancelMemoryItemEdit();
    },
    async loadSessionBackups() {
      this.sessionBackupLoading = true;
      this.sessionBackupError = '';
      try {
        this.sessionBackups = await sessionBackupDB.listDailySnapshots();
      } catch (error) {
        console.error('Failed to load local chat history backups:', error);
        this.sessionBackups = [];
        this.sessionBackupError = error.message || String(error);
      } finally {
        this.sessionBackupLoading = false;
      }
    },
    async viewSessionBackup(date) {
      try {
        const snapshot = await sessionBackupDB.getDailySnapshot(date);
        if (!snapshot) throw new Error(this.$t('settingsModal.localSessionBackups.notFound'));
        this.sessionBackupPreview = {
          date: snapshot.date,
          sessions: Array.isArray(snapshot.sessions) ? snapshot.sessions : []
        };
        this.selectedBackupSessionId = this.sessionBackupPreview.sessions.length
          ? this.sessionBackupPreview.sessions[0].id
          : null;
      } catch (error) {
        console.error('Failed to open local chat history backup:', error);
        alert(this.$t('settingsModal.localSessionBackups.actionFailed', { message: error.message || String(error) }));
      }
    },
    async restoreSessionBackup(date) {
      if (this.sessionBackupBusy) return;
      this.sessionBackupBusy = true;
      try {
        const snapshot = await sessionBackupDB.getDailySnapshot(date);
        if (!snapshot || !Array.isArray(snapshot.sessions) || !snapshot.sessions.length) {
          throw new Error(this.$t('settingsModal.localSessionBackups.notFound'));
        }
        const restoredIds = new Set(snapshot.sessions.map(session => String(session.id)));
        const currentSessionId = store.currentSessionId == null ? null : String(store.currentSessionId);
        await Promise.all(snapshot.sessions.map(session => memoryDB.saveSession(session)));
        await mutations.loadMemory();
        if (currentSessionId && restoredIds.has(currentSessionId)) {
          await mutations.loadSession(currentSessionId);
        }
        await this.loadSessionBackups();
        alert(this.$t('settingsModal.localSessionBackups.restoreSuccess', { count: snapshot.sessions.length }));
      } catch (error) {
        console.error('Failed to restore local chat history backup:', error);
        alert(this.$t('settingsModal.localSessionBackups.actionFailed', { message: error.message || String(error) }));
      } finally {
        this.sessionBackupBusy = false;
      }
    },
    formatBackupMessage(content) {
      if (content == null) return '';
      if (typeof content === 'string') return content;
      try {
        return JSON.stringify(content, null, 2);
      } catch (error) {
        return String(content);
      }
    },
    cancelMemoryEdit(section) {
      if (section === 'soul') {
        this.memorySoulDraft = this.memorySoulText;
        this.memorySoulEditing = false;
      } else {
        this.memoryProfileDraft = this.memoryProfileText;
        this.memoryProfileEditing = false;
      }
    },
    async saveMemorySection(section) {
      const isSoul = section === 'soul';
      const current = isSoul ? store.soul : store.user;
      const key = isSoul ? 'personality' : 'profile';
      const draft = (isSoul ? this.memorySoulDraft : this.memoryProfileDraft).trim();
      let nextValue;

      if (current && typeof current === 'object' && !Array.isArray(current)) {
        nextValue = JSON.parse(JSON.stringify(current));
        if (draft.startsWith('{')) {
          try {
            const parsed = JSON.parse(draft);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('invalid section');
            nextValue = { ...nextValue, ...parsed };
          } catch (error) {
            if (draft.trim() === '') nextValue[key] = '';
            else if (draft.trim().startsWith('{')) {
              await alert(this.$t('settingsModal.memory.invalidJson'));
              return;
            } else nextValue[key] = draft;
          }
        } else {
          nextValue[key] = draft;
        }
      } else {
        nextValue = draft;
      }

      await mutations.updateCoreMemory(isSoul ? 'soul' : 'user', nextValue);
      if (isSoul) this.memorySoulEditing = false;
      else this.memoryProfileEditing = false;
      await alert(this.$t('settingsModal.memory.saved'));
    },
    formatMemoryItem(item) {
      if (typeof item === 'string') return item;
      if (item == null) return '';
      return JSON.stringify(item, null, 2);
    },
    editMemoryItem(item) {
      this.memoryItemEditingId = item.id;
      this.memoryItemAdding = false;
      this.memoryItemDraft = this.formatMemoryItem(item.value);
    },
    startAddMemoryItem() {
      this.memoryItemEditingId = null;
      this.memoryItemAdding = true;
      this.memoryItemDraft = '';
    },
    cancelMemoryItemEdit() {
      this.memoryItemEditingIndex = null;
      this.memoryItemAdding = false;
      this.memoryItemDraft = '';
    },
    parseMemoryItem(draft, previousItem) {
      const text = draft.trim();
      if (typeof previousItem !== 'string' && (text.startsWith('{') || text.startsWith('['))) {
        try { return JSON.parse(text); } catch (error) { return text; }
      }
      return text;
    },
    async persistMemoryItems(items) {
      const nextMemory = Array.isArray(store.memory) ? items : {
        ...(store.memory && typeof store.memory === 'object' ? store.memory : {}),
        longTerm: items
      };
      await mutations.updateCoreMemory('memory', nextMemory);
    },
    async saveMemoryItem(item) {
      const text = this.memoryItemDraft.trim();
      if (!text) return;
      const value = this.parseMemoryItem(text, item.value);
      if (item.source === 'field') {
        const nextMemory = { ...store.memory, [item.key]: value };
        await mutations.updateCoreMemory('memory', nextMemory);
      } else if (item.source === 'scalar') {
        await mutations.updateCoreMemory('memory', value);
      } else {
        const items = item.source === 'array'
          ? JSON.parse(JSON.stringify(store.memory))
          : JSON.parse(JSON.stringify(store.memory.longTerm));
        items[item.index] = value;
        await this.persistMemoryItems(items);
      }
      this.cancelMemoryItemEdit();
    },
    async addMemoryItem() {
      const text = this.memoryItemDraft.trim();
      if (!text) return;
      const items = JSON.parse(JSON.stringify(this.memoryItems));
      items.push(this.parseMemoryItem(text));
      await this.persistMemoryItems(items);
      this.cancelMemoryItemEdit();
    },
    async deleteMemoryItem(item) {
      if (!await confirm(this.$t('settingsModal.memory.deleteConfirm'))) return;
      if (item.source === 'field') {
        const nextMemory = { ...store.memory };
        delete nextMemory[item.key];
        await mutations.updateCoreMemory('memory', nextMemory);
      } else if (item.source === 'scalar') {
        await mutations.updateCoreMemory('memory', { longTerm: [] });
      } else {
        const items = item.source === 'array'
          ? JSON.parse(JSON.stringify(store.memory))
          : JSON.parse(JSON.stringify(store.memory.longTerm));
        items.splice(item.index, 1);
        await this.persistMemoryItems(items);
      }
    },
    async clearMemoryData() {
      if (!await confirm(this.$t('settingsModal.memory.clearConfirm'))) return;
      await mutations.updateCoreMemory('soul', { personality: 'You are a helpful AI assistant.' });
      await mutations.updateCoreMemory('user', { profile: 'User' });
      await mutations.updateCoreMemory('memory', Array.isArray(store.memory) ? [] : { ...(store.memory || {}), longTerm: [] });
      this.prepareMemoryEditors();
      await alert(this.$t('settingsModal.memory.cleared'));
    },
    close() {
      this.closeTokenStore();
      this.$emit('close');
    },
    setTheme(theme) {
      mutations.setTheme(theme);
    },
    syncTokenStoreUrlMarker(isActive) {
      if (typeof window === 'undefined' || !window.history || !window.location) {
        return;
      }

      const url = new URL(window.location.href);

      if (isActive) {
        if (this.tokenStoreUrlMarkerValues === null) {
          this.tokenStoreUrlMarkerHadValue = url.searchParams.has(TOKEN_STORE_URL_MARKER_KEY);
          this.tokenStoreUrlMarkerValues = url.searchParams.getAll(TOKEN_STORE_URL_MARKER_KEY);
        }

        url.searchParams.delete(TOKEN_STORE_URL_MARKER_KEY);
        url.searchParams.append(TOKEN_STORE_URL_MARKER_KEY, TOKEN_STORE_URL_MARKER_VALUE);
      } else {
        if (this.tokenStoreUrlMarkerValues === null && !url.searchParams.has(TOKEN_STORE_URL_MARKER_KEY)) {
          return;
        }

        url.searchParams.delete(TOKEN_STORE_URL_MARKER_KEY);
        if (this.tokenStoreUrlMarkerHadValue && Array.isArray(this.tokenStoreUrlMarkerValues)) {
          this.tokenStoreUrlMarkerValues.forEach((value) => {
            url.searchParams.append(TOKEN_STORE_URL_MARKER_KEY, value);
          });
        }

        this.tokenStoreUrlMarkerHadValue = false;
        this.tokenStoreUrlMarkerValues = null;
      }

      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    },
    restoreTokenStoreFromUrl() {
      if (typeof window === 'undefined' || !window.location) {
        return;
      }

      let values = [];
      try {
        values = new URLSearchParams(window.location.search).getAll(TOKEN_STORE_URL_MARKER_KEY);
      } catch (error) {
        return;
      }

      if (!values.includes(TOKEN_STORE_URL_MARKER_VALUE)) {
        return;
      }

      // 地址参数来自刷新前的打开状态：视为本次会话自身的标记，
      // 关闭商店时直接从地址栏移除，而不是还原旧值。
      this.tokenStoreUrlMarkerHadValue = false;
      this.tokenStoreUrlMarkerValues = [];
      this.showTokenStore = true;
    },
    resetFetchedModelOptions() {
      this.modelListLoading = false;
      this.modelListError = '';
      this.modelListHint = '';
      this.availableModels = [];
      this.modelDropdownOpen = false;
      this.highlightedModelIndex = -1;
      this.selectedFetchedModel = '';
      this.selectedFetchedModels = [];
      this.modelSearchQuery = '';
      this.customModelOptions = [];
      this.lastModelCatalogSource = '';
    },
    modelCatalogCacheKey(model = this.modelEditForm) {
      if (model && model.id && model.id !== 'new') return `config:${model.id}`;
      return `catalog:${model && model.provider ? model.provider : ''}|${model && model.type ? model.type : 'text'}|${model && model.endpoint ? model.endpoint : ''}`;
    },
    readModelCatalogCache(model) {
      if (typeof localStorage === 'undefined') return [];
      try {
        const cache = JSON.parse(localStorage.getItem(MODEL_CATALOG_CACHE_KEY) || '{}');
        const models = cache[this.modelCatalogCacheKey(model)];
        return Array.isArray(models) ? models : [];
      } catch (error) {
        return [];
      }
    },
    writeModelCatalogCache(model, models) {
      if (typeof localStorage === 'undefined' || !Array.isArray(models) || !models.length) return;
      try {
        const cache = JSON.parse(localStorage.getItem(MODEL_CATALOG_CACHE_KEY) || '{}');
        cache[this.modelCatalogCacheKey(model)] = models.map(item => ({ ...item }));
        localStorage.setItem(MODEL_CATALOG_CACHE_KEY, JSON.stringify(cache));
      } catch (error) {
        // Catalog caching is a convenience and should never block model editing.
      }
    },
    openModelDropdown() {
      if (!this.availableModels.length) return;
      this.modelDropdownOpen = true;
      const selectedIndex = this.filteredAvailableModels.findIndex(item => item.id === this.selectedFetchedModel);
      this.highlightedModelIndex = selectedIndex >= 0 ? selectedIndex : (this.filteredAvailableModels.length ? 0 : -1);
    },
    closeModelDropdown() {
      this.modelDropdownOpen = false;
      this.highlightedModelIndex = -1;
    },
    toggleModelDropdown() {
      if (!this.availableModels.length) return;
      if (this.modelDropdownOpen) {
        this.closeModelDropdown();
        return;
      }
      this.openModelDropdown();
      this.$nextTick(() => {
        if (this.$refs.modelInput) {
          this.$refs.modelInput.focus();
        }
      });
    },
    handleModelInputFocus() {
      if (this.availableModels.length) {
        this.openModelDropdown();
      }
    },
    handleModelInputChange() {
      const matchedModel = this.availableModels.find(item => item.id === this.modelEditForm.model);
      this.selectedFetchedModel = matchedModel ? matchedModel.id : '';

      if (this.availableModels.length) {
        this.modelDropdownOpen = true;
        this.highlightedModelIndex = this.filteredAvailableModels.length ? 0 : -1;
      }
    },
    selectFetchedModel(item) {
      if (!item) return;
      this.selectedFetchedModel = item.id;
      this.selectedFetchedModels = [item.id];
      this.modelEditForm.model = item.id;
      this.closeModelDropdown();
    },
    isFetchedModelSelected(id) {
      return this.selectedFetchedModels.includes(id);
    },
    addCustomModel() {
      const id = (this.modelSearchQuery || '').trim();
      if (!id) return;
      const existing = this.pickerModels.find(item => item.id === id);
      if (!existing) {
        this.customModelOptions = [...this.customModelOptions, { id, label: id, displayName: '', ownedBy: '', pinned: false, custom: true }];
      }
      if (!this.selectedFetchedModels.includes(id)) {
        this.selectedFetchedModels = [...this.selectedFetchedModels, id];
      }
      this.modelEditForm.model = id;
      this.modelSearchQuery = '';
    },
    toggleFetchedModel(item) {
      if (!item) return;
      const selected = this.selectedFetchedModels.slice();
      const index = selected.indexOf(item.id);
      if (index >= 0) selected.splice(index, 1);
      else selected.push(item.id);
      this.selectedFetchedModels = selected;
      if (!this.selectedFetchedModels.includes(this.modelEditForm.model)) {
        this.modelEditForm.model = this.selectedFetchedModels[0] || '';
      }
    },
    toggleSelectAllFetchedModels() {
      if (this.allFetchedModelsSelected) {
        this.selectedFetchedModels = [];
        this.modelEditForm.model = '';
        return;
      }
      this.selectedFetchedModels = this.pickerModels.map(item => item.id);
      if (!this.selectedFetchedModels.includes(this.modelEditForm.model)) {
        this.modelEditForm.model = this.selectedFetchedModels[0] || '';
      }
    },
    startSelectedModelDrag(item, event) {
      this.draggedSelectedModelId = item && item.id ? item.id : '';
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', this.draggedSelectedModelId);
      }
    },
    dragOverSelectedModel(item, event) {
      this.dragOverSelectedModelId = item && item.id ? item.id : '';
      if (event && event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    },
    dropSelectedModel(item) {
      const sourceId = this.draggedSelectedModelId;
      const targetId = item && item.id;
      this.draggedSelectedModelId = '';
      this.dragOverSelectedModelId = '';
      if (!sourceId || !targetId || sourceId === targetId) return;
      const sourceIndex = this.selectedFetchedModels.indexOf(sourceId);
      const targetIndex = this.selectedFetchedModels.indexOf(targetId);
      if (sourceIndex < 0 || targetIndex < 0) return;
      const next = this.selectedFetchedModels.slice();
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      this.selectedFetchedModels = next;
    },
    moveModelDropdown(step) {
      if (!this.availableModels.length) return;
      if (!this.modelDropdownOpen) {
        this.openModelDropdown();
        return;
      }

      const list = this.filteredAvailableModels;
      if (!list.length) {
        this.highlightedModelIndex = -1;
        return;
      }

      const lastIndex = list.length - 1;
      if (this.highlightedModelIndex < 0) {
        this.highlightedModelIndex = step > 0 ? 0 : lastIndex;
        return;
      }

      const nextIndex = this.highlightedModelIndex + step;
      if (nextIndex < 0) {
        this.highlightedModelIndex = lastIndex;
      } else if (nextIndex > lastIndex) {
        this.highlightedModelIndex = 0;
      } else {
        this.highlightedModelIndex = nextIndex;
      }
    },
    confirmHighlightedModel() {
      if (!this.modelDropdownOpen) return;
      const item = this.filteredAvailableModels[this.highlightedModelIndex];
      if (item) {
        this.selectFetchedModel(item);
      } else {
        this.closeModelDropdown();
      }
    },
    handleDocumentClick(event) {
      if (!event.target.closest('.simple-model-more')) {
        this.closeManagementModelMenu();
      }
      const root = this.$refs.modelCombobox;
      if (!root) return;
      if (root.contains(event.target)) return;
      this.closeModelDropdown();
    },
    async fetchModelList() {
      if (this.modelListLoading) return;

      const validation = validateModelCatalogEndpoint(this.modelEditForm.endpoint);
      if (!validation.ok) {
        if (validation.code === MODEL_CATALOG_VALIDATION_ERRORS.NEED_ENDPOINT) {
          this.modelListError = this.$t('settingsModal.fetchModelsNeedEndpoint');
        } else if (validation.code === MODEL_CATALOG_VALIDATION_ERRORS.INVALID_ENDPOINT) {
          this.modelListError = this.$t('settingsModal.fetchModelsInvalidEndpoint');
        } else if (validation.code === MODEL_CATALOG_VALIDATION_ERRORS.NEED_REAL_HOST) {
          this.modelListError = this.$t('settingsModal.fetchModelsNeedRealHost');
        } else {
          this.modelListError = this.$t('settingsModal.fetchModelsFailed');
        }
        this.modelListHint = '';
        return;
      }

      if (!String(this.modelEditForm.apiKey || '').trim()) {
        this.modelListError = this.$t('settingsModal.fetchModelsNeedApiKey');
        this.modelListHint = '';
        return;
      }

      const endpoint = validation.endpoint;
      this.modelListLoading = true;
      this.modelListError = '';
      this.modelListHint = '';
      this.availableModels = [];
      this.selectedFetchedModel = '';
      this.selectedFetchedModels = [];
      this.lastModelCatalogSource = '';

      try {
        const { models, sourceHost } = await requestModelCatalog({
          provider: this.modelEditForm.provider || '',
          type: this.modelEditForm.type || 'text',
          endpoint,
          apiKey: this.modelEditForm.apiKey || ''
        });

        const previousById = new Map((this.modelEditForm.modelOptions || []).map(item => [item.id, item]));
        this.availableModels = models.map(item => ({
          ...item,
          pinned: !!previousById.get(item.id)?.pinned
        }));
        this.writeModelCatalogCache(this.modelEditForm, this.availableModels);
        this.lastModelCatalogSource = sourceHost;

        if (!models.length) {
          this.modelListHint = this.$t('settingsModal.modelListEmpty');
          this.closeModelDropdown();
          return;
        }

        this.modelListHint = this.$t('settingsModal.fetchedModelsHint', { count: models.length });
        const existingIds = [
          ...(Array.isArray(this.modelEditForm.modelOptions) ? this.modelEditForm.modelOptions.map(item => item.id) : []),
          ...this.selectedFetchedModels
        ];
        // Keep existing custom/legacy options selectable even when the refreshed catalog does not return them.
        const availableIds = new Set(this.pickerModels.map(item => item.id));
        const preserved = existingIds.filter(id => availableIds.has(id));
        this.selectedFetchedModels = preserved.length
          ? preserved
          : (models.length === 1 ? [models[0].id] : (this.modelEditForm.model && availableIds.has(this.modelEditForm.model) ? [this.modelEditForm.model] : [models[0].id]));
        this.modelEditForm.model = this.selectedFetchedModels.includes(this.modelEditForm.model)
          ? this.modelEditForm.model
          : (this.selectedFetchedModels[0] || '');
        this.selectedFetchedModel = this.modelEditForm.model;
      } catch (e) {
        this.modelListError = this.$t('settingsModal.fetchModelsFailedWithMessage', {
          message: e && e.message ? e.message : this.$t('settingsModal.fetchModelsFailed')
        });
      } finally {
        this.modelListLoading = false;
      }
    },
    // Model Management Methods
    getModelsByType(type) {
      const models = type === 'text'
        ? store.settings.models.filter(m => m.type === 'text' || !m.type)
        : store.settings.models.filter(m => m.type === type);
      return models.slice().sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
    },
    formatTokenStoreBalance(model) {
      const tokenId = model.tokenStore && model.tokenStore.tokenId;
      const cached = tokenId ? this.tokenStoreBalances[tokenId] : null;
      if (!tokenId) return this.$t('settingsModal.purchasedModel');
      if (!cached) return this.$t('settingsModal.balanceLoading');
      if (cached.error) return this.$t('settingsModal.balanceUnavailable');
      if (cached.remainAmount !== null && cached.remainAmount !== undefined) {
        return this.$t('settingsModal.balanceAmount', { amount: cached.remainAmount });
      }
      const amount = ((Number(cached.remainQuota) || 0) / 500000).toFixed(2);
      return this.$t('settingsModal.balanceAmount', { amount });
    },
    isModelActive(modelId, type) {
      if (type === 'text') return store.settings.activeTextModelId === modelId;
      if (type === 't2i') return store.settings.activeT2iModelId === modelId;
      if (type === 'i2i') return store.settings.activeI2iModelId === modelId;
      return false;
    },
    isCategoryExpanded(type) {
      return this.expandedModelCategories[type] !== false;
    },
    toggleModelCategory(type) {
      this.$set(this.expandedModelCategories, type, !this.isCategoryExpanded(type));
    },
    getActiveModelByType(type) {
      return this.getModelsByType(type).find(model => this.isModelActive(model.id, type));
    },
    selectModel(id) {
      mutations.setActiveModel(id);
    },
    selectModelByType(id, type) {
      mutations.setActiveModelByType(id, type);
    },
    startManagementModelDrag(model, event) {
      this.draggedManagementModelId = model && model.id ? model.id : '';
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', this.draggedManagementModelId);
      }
    },
    dragOverManagementModel(model, event) {
      this.dragOverManagementModelId = model && model.id ? model.id : '';
      if (event && event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    },
    dropManagementModel(model, type) {
      const sourceId = this.draggedManagementModelId;
      this.draggedManagementModelId = '';
      this.dragOverManagementModelId = '';
      if (!sourceId || !model || sourceId === model.id) return;
      mutations.reorderModelConfigs(type, sourceId, model.id);
    },
    toggleManagementModelPinned(model) {
      if (model && model.id) mutations.toggleModelConfigPinned(model.id);
    },
    toggleManagementModelMenu(id) {
      this.managementModelMenuId = this.managementModelMenuId === id ? '' : id;
    },
    closeManagementModelMenu() {
      this.managementModelMenuId = '';
    },
    toggleSelectedModelPinned(item) {
      if (!item || !item.id) return;
      const options = this.modelEditForm.modelOptions || [];
      const existing = options.find(option => option.id === item.id);
      const nextPinned = !(existing ? existing.pinned : item.pinned);
      const nextOptions = options.map(option => option.id === item.id ? { ...option, pinned: nextPinned } : option);
      if (!existing) nextOptions.push({ ...item, pinned: nextPinned });
      this.modelEditForm.modelOptions = nextOptions;
      const availableIndex = this.availableModels.findIndex(option => option.id === item.id);
      if (availableIndex >= 0) this.$set(this.availableModels, availableIndex, { ...this.availableModels[availableIndex], pinned: nextPinned });
      const customIndex = this.customModelOptions.findIndex(option => option.id === item.id);
      if (customIndex >= 0) this.$set(this.customModelOptions, customIndex, { ...this.customModelOptions[customIndex], pinned: nextPinned });
      const selected = this.selectedFetchedModels.filter(id => id !== item.id);
      if (nextPinned) selected.unshift(item.id);
      else selected.push(item.id);
      this.selectedFetchedModels = selected;
    },
    async openTokenStore() {
      this.showTokenStore = true;
    },
    closeTokenStore() {
      this.showTokenStore = false;
    },
    async refreshTokenStoreBalances() {
      const purchasedModels = store.settings.models.filter(model => model.tokenStore && model.tokenStore.tokenId);
      await Promise.all(purchasedModels.map(async (model) => {
        const tokenId = model.tokenStore.tokenId;
        try {
          const response = await fetch(`${TOKEN_STORE_API_BASE}/balance?tokenId=${encodeURIComponent(tokenId)}&token=${encodeURIComponent(store.token || '')}`);
          const result = await response.json();
          if (result.code !== 0) {
            throw new Error(result.msg || 'balance error');
          }
          this.$set(this.tokenStoreBalances, tokenId, result.data);
        } catch (e) {
          this.$set(this.tokenStoreBalances, tokenId, { error: true });
        }
      }));
    },
    startAddModel() {
      this.editingModelId = 'new';
      this.resetFetchedModelOptions();
      this.modelEditForm = {
        name: '',
        provider: '',
        type: 'text',
        endpoint: '',
        apiKey: '',
        model: '',
        modelOptions: []
      };
    },
    startEditModel(model) {
      this.editingModelId = model.id;
      this.resetFetchedModelOptions();
      this.modelEditForm = {
        provider: '', // Default to empty, will be loaded from model if exists
        type: 'text', // Default to text
        ...model,
        modelOptions: Array.isArray(model.modelOptions) && model.modelOptions.length
          ? model.modelOptions.map(option => ({ ...option }))
          : (model.model ? [{ id: model.model, label: model.model }] : [])
      };
      const cachedModels = this.readModelCatalogCache(this.modelEditForm);
      const cachedIds = new Set(cachedModels.map(option => option.id));
      this.availableModels = cachedModels.map(item => {
        const selectedOption = this.modelEditForm.modelOptions.find(option => option.id === item.id);
        return selectedOption ? { ...item, ...selectedOption } : { ...item };
      });
      this.customModelOptions = this.modelEditForm.modelOptions
        .filter(option => !cachedIds.has(option.id))
        .map(option => ({ ...option, custom: true }));
      this.selectedFetchedModels = this.modelEditForm.modelOptions.map(option => option.id);
      if (this.availableModels.length || this.customModelOptions.length) {
        this.modelListHint = this.availableModels.length ? this.$t('settingsModal.cachedModelsHint', { count: this.availableModels.length }) : '';
      }
    },
    handleProviderChange() {
      this.resetFetchedModelOptions();
      if (!this.modelEditForm.provider) return;
      
      const provider = this.providers.find(p => p.name === this.modelEditForm.provider || p.id === this.modelEditForm.provider);
      if (provider) {
        // Auto-fill name if empty
        this.modelEditForm.name = this.modelEditForm.name || provider.name;
        
        // Check if current type is supported by the provider, if not default to 'text'
        if (this.modelEditForm.type && !provider.configs[this.modelEditForm.type]) {
          this.modelEditForm.type = 'text';
        }
        
        // Use current type or default to 'text'
        const type = this.modelEditForm.type || 'text';
        const config = provider.configs[type] || provider.configs['text'];
        
        if (config) {
          this.modelEditForm.endpoint = config.endpoint;
        }
      }
      // 切换服务商后不再默认填充模型，需手动输入或点击获取
      this.modelEditForm.model = '';
      this.modelEditForm.modelOptions = [];
    },
    handleTypeChange() {
      this.resetFetchedModelOptions();
      // If a provider is selected, update endpoint based on the new type
      if (this.modelEditForm.provider) {
        const provider = this.providers.find(p => p.name === this.modelEditForm.provider || p.id === this.modelEditForm.provider);
        if (provider) {
          const config = provider.configs[this.modelEditForm.type];
          if (config) {
            this.modelEditForm.endpoint = config.endpoint;
          }
        }
      } else {
        // Handle custom provider defaults for different types if needed
        if (this.modelEditForm.type === 'text') {
          this.modelEditForm.endpoint = this.modelEditForm.endpoint || 'https://api.openai.com/v1/chat/completions';
        }
      }
      // 切换类型后不再默认填充模型，需手动输入或点击获取
      this.modelEditForm.model = '';
      this.modelEditForm.modelOptions = [];
    },
    cancelEditModel() {
      this.resetFetchedModelOptions();
      this.editingModelId = null;
    },
    saveModel() {
      const selectedOptions = this.pickerModels.length
        ? this.selectedFetchedModels
          .map(id => this.pickerModels.find(item => item.id === id))
          .filter(Boolean)
          .map(item => ({
            id: item.id,
            label: item.label || item.id,
            displayName: item.displayName || '',
            ownedBy: item.ownedBy || '',
            pinned: !!item.pinned
          }))
        : (this.modelEditForm.modelOptions.length
          ? this.modelEditForm.modelOptions.map(option => ({ ...option }))
          : (this.modelEditForm.model ? [{ id: this.modelEditForm.model, label: this.modelEditForm.model, pinned: false }] : []));
      const previousOptions = this.modelEditForm.modelOptions || [];
      const previousById = new Map(previousOptions.map(option => [option.id, option]));
      const modelOptions = selectedOptions.map(option => ({
        ...option,
        pinned: previousById.has(option.id) ? !!previousById.get(option.id).pinned : !!option.pinned
      }));
      const activeModel = modelOptions.find(option => option.id === this.modelEditForm.model) || modelOptions[0];

      if (!this.modelEditForm.name || !this.modelEditForm.endpoint || !activeModel) {
        alert(this.$t('settingsModal.fillRequired'));
        return;
      }

      const payload = {
        ...this.modelEditForm,
        model: activeModel.id,
        modelOptions
      };

      if (this.editingModelId === 'new') {
        const savedModel = mutations.addModel(payload);
        this.writeModelCatalogCache(savedModel, this.availableModels);
      } else {
        mutations.updateModel(this.editingModelId, payload);
        this.writeModelCatalogCache({ ...payload, id: this.editingModelId }, this.availableModels);
      }
      this.editingModelId = null;
    },
    async confirmDeleteModel(id) {
      if (await confirm(this.$t('settingsModal.deleteModelConfirm'))) {
        mutations.deleteModel(id);
      }
    },
    updateBackupReminderDays(value) {
      const days = parseInt(value, 10);
      if (days > 0) {
        mutations.setBackupReminderDays(days);
      }
    },
    async promptBackupDays() {
      const current = this.store.settings.backupReminderDays;
      const val = await showPrompt(this.$t('settingsModal.backupReminderPrompt'), current);
      if (val !== null) {
        this.updateBackupReminderDays(val);
      }
    },
    toggleCloudSync() {
      mutations.setCloudSyncEnabled(!this.store.settings.cloudSyncEnabled);
    },
    // Cloud Sync Methods
    handleLoginClick() {
      this.$emit('open-login');
    },
    logout() {
      mutations.logout();
    },
    async uploadCloudBackup() {
      if (!store.token) return;
      this.cloudLoading = true;
      try {
        const backupData = await mutations.getFullBackup();
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: JSON.stringify({
            "token": store.token,
             action: 'setCloudBackup',
             data: JSON.stringify(backupData)
          })
        };

        const response = await fetch(BACKUP_API_URL, requestOptions);
        const result = await response.json();
        
        if (result.logout || result.code === 401) {
          this.logout();
          alert(this.$t('settingsModal.loginExpired'));
          return;
        }

        if (result.code === 0 || result.status === 'success' || (result.data && result.data.code === 0)) {
          alert(this.$t('settingsModal.cloudBackupSuccess'));
          mutations.updateLastBackupTime();
        } else {
          throw new Error(result.message || this.$t('settingsModal.backupFailed'));
        }
      } catch (e) {
        console.error('Cloud backup failed:', e);
        alert(this.$t('settingsModal.cloudBackupFailed', { message: e.message }));
      } finally {
        this.cloudLoading = false;
      }
    },
    async downloadCloudBackup() {
      if (!store.token) return;
      if (!await confirm(this.$t('settingsModal.cloudRestoreConfirm'))) return;
      
      this.cloudLoading = true;
      try {
        const myHeaders = new Headers();
       
        myHeaders.append("Content-Type", "application/json");

        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: JSON.stringify({
            "token": store.token,
             action: 'getCloudBackup'
          })
        };

        const response = await fetch(BACKUP_API_URL, requestOptions);
        const result = await response.json();
        
        if (result.logout || result.code === 401) {
          this.logout();
          alert(this.$t('settingsModal.loginExpired'));
          return;
        }

        let cloudData = null;
        if (result.data && result.data.ret) {
           cloudData = JSON.parse(result.data.ret);
        } else if (result.ret) {
           cloudData = JSON.parse(result.ret);
        } else if (typeof result === 'object' && result.store) {
           cloudData = result;
        }

        if (cloudData) {
          await mutations.restoreFromBackup(cloudData);
          await alert(this.$t('settingsModal.cloudRestoreSuccess'));
          window.location.reload();
        } else {
          alert(this.$t('settingsModal.cloudBackupNotFound'));
        }
      } catch (e) {
        console.error('Cloud restore failed:', e);
        alert(this.$t('settingsModal.cloudRestoreFailed', { message: e.message }));
      } finally {
        this.cloudLoading = false;
      }
    },
    // Backup & Restore Methods
    async exportData() {
      try {
        const backup = await mutations.getFullBackup();
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
        const date = new Date().toISOString().split('T')[0];
        saveAs(blob, `ai-assistant-backup-${date}.json`);
      } catch (e) {
        console.error('Export failed', e);
        alert(this.$t('settingsModal.exportFailed', { message: e.message }));
      }
    },
    async importData(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
        reader.onload = async (e) => {
        try {
          const backup = JSON.parse(e.target.result);
          if (await confirm(this.$t('settingsModal.importConfirm'))) {
            await mutations.restoreFromBackup(backup);
            await alert(this.$t('settingsModal.importSuccess'));
            window.location.reload();
          }
        } catch (err) {
          console.error('Import failed', err);
          alert(this.$t('settingsModal.importFailed'));
        }
      };
      reader.readAsText(file);
      event.target.value = ''; // Reset file input
    },
    async resetAllData() {
      if (await confirm(this.$t('settingsModal.clearAllConfirm'))) {
        // [DEBUG-TEMP] localStorage.clear() 会连带清掉 localStorage['token']，且不经过 mutations.logout()
        console.warn('[token-debug] resetAllData() 即将 localStorage.clear()，登录态会被一并清除', new Error().stack);
        debugger;
        localStorage.clear();
        await memoryDB.clearAll();
        await sessionBackupDB.clearAll();
        await alert(this.$t('settingsModal.clearAllSuccess'));
        window.location.reload();
      }
    }
  }
};
</script>

<style scoped>
.local-session-backup-row, .local-session-backup-info, .local-session-backup-actions, .local-session-preview-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.local-session-backup-info { min-width: 0; flex-direction: column; align-items: flex-start; gap: 4px; }
.local-session-backup-info strong, .local-session-preview-heading strong { color: var(--text-main, #1e293b); font-size: 14px; }
.local-session-backup-info span { color: var(--text-secondary, #64748b); font-size: 12px; }
.local-session-backup-actions { flex-shrink: 0; }
.local-session-backup-actions button:disabled { opacity: .5; cursor: wait; }
.local-session-preview-heading { margin-bottom: 12px; }
.local-session-preview-list { display: flex; gap: 8px; overflow-x: auto; padding: 2px 0 10px; }
.local-session-preview-item { display: flex; flex: 0 0 auto; flex-direction: column; gap: 4px; max-width: 220px; padding: 9px 12px; border: 1px solid var(--border-color, rgba(0,0,0,.08)); border-radius: 10px; background: transparent; color: var(--text-main, #1e293b); text-align: left; cursor: pointer; }
.local-session-preview-item.active { border-color: var(--primary-blue, #2f54eb); background: var(--soft-blue, #eef2ff); }
.local-session-preview-item span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.local-session-preview-item small { color: var(--text-secondary, #64748b); font-size: 11px; }
.local-session-transcript { max-height: 320px; overflow: auto; padding: 12px; border-radius: 12px; background: var(--bg-main, #f4f7fc); }
.local-session-message + .local-session-message { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color, rgba(0,0,0,.08)); }
.local-session-message strong { display: block; margin-bottom: 5px; color: var(--text-secondary, #64748b); font-size: 11px; text-transform: uppercase; }
.local-session-message pre { margin: 0; color: var(--text-main, #1e293b); font: inherit; font-size: 13px; line-height: 1.55; white-space: pre-wrap; overflow-wrap: anywhere; }
.local-session-backup-preview { border-top: 1px solid var(--border-color, rgba(0,0,0,.08)); }
@media (max-width: 520px) {
  .local-session-backup-row { align-items: flex-start; flex-direction: column; }
}

/* Modern Soft & Rounded Style Variables */
.modal-overlay {
  --primary-blue: #2f54eb;
  --primary-blue-hover: #2644c5;
  --soft-blue: #eef2ff;
  --bg-main: #f4f7fc;
  --bg-sidebar: #ffffff;
  --bg-card: #ffffff;
  --text-main: #1e293b;
  --text-secondary: #64748b;
  --border-color: rgba(0, 0, 0, 0.08);
  --shadow-sm: none;
  --shadow-md: none;
  --shadow-lg: none;
  --radius-xl: 12px;
  --radius-lg: 8px;
  --radius-md: 4px;
  --radius-pill: 9999px;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

[data-theme="dark"] .modal-overlay {
  --primary-blue: #4f71ff;
  --primary-blue-hover: #6b8aff;
  --soft-blue: rgba(79, 113, 255, 0.1);
  --bg-main: #0f111a;
  --bg-sidebar: #161925;
  --bg-card: #1a1d27;
  --text-main: #f1f5f9;
  --text-secondary: #94a3b8;
  --border-color: rgba(255, 255, 255, 0.1);
  --shadow-sm: none;
  --shadow-md: none;
  --shadow-lg: none;

  background-color: rgba(0, 0, 0, 0.6);
}

.modal-container {
  width: 90%;
  max-width: 860px;
  height: 82vh;
  max-height: 680px;
  background: var(--bg-main);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  display: flex;
  overflow: hidden;
  border: 1px solid var(--border-color);
  color: var(--text-main);
  position: relative;
}

.modal-sidebar {
  background-color: var(--bg-sidebar);
  width: 220px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  z-index: 10;
  border-right: 1px solid var(--border-color);
}

.modal-header {
  padding-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-weight: 700;
  margin: 0;
  color: var(--text-main);
  letter-spacing: -0.5px;
}

.settings-nav {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-nav li {
  padding: 12px 16px;
  text-align: left;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 600;
  color: var(--text-secondary);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.settings-nav li:hover {
  background-color: var(--soft-blue);
  color: var(--primary-blue);
}

.settings-nav li.active {
  background-color: var(--primary-blue);
  color: #fff;
}

.modal-content {
  flex: 1;
  padding: 2.5rem 3rem;
  overflow-y: auto;
  position: relative;
}

.modal-close-btn {
  padding: 0.6rem;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  color: var(--text-secondary);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  flex-shrink: 0;
}

.modal-close-btn svg {
  fill: currentColor;
}

.modal-close-btn:hover {
  color: var(--primary-blue);
  background: var(--soft-blue);
  transform: rotate(90deg);
}

.settings-panel h4 {
  margin-top: 0;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.memory-page-heading, .memory-card-heading, .memory-clear-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.memory-page-heading { margin-bottom: 1rem; }
.memory-page-heading h4 { margin-bottom: 0.3rem; }
.memory-page-heading p, .memory-card-heading p, .memory-clear-row p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary, #718096);
  font-size: 0.88rem;
  line-height: 1.5;
}
.memory-local-badge, .memory-count {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  background: rgba(52, 168, 120, 0.12);
  color: #23845d;
  font-size: 0.78rem;
  font-weight: 600;
}
.memory-privacy-notice {
  display: flex;
  gap: 0.85rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(52, 168, 120, 0.22);
  border-radius: 12px;
  background: rgba(52, 168, 120, 0.07);
  color: var(--text-primary, #2d3748);
}
.memory-privacy-icon { font-size: 1.3rem; line-height: 1.3; color: #23845d; }
.memory-privacy-notice strong { font-size: 0.9rem; }
.memory-privacy-notice p { margin: 0.35rem 0 0; color: var(--text-secondary, #5f6b7a); font-size: 0.84rem; line-height: 1.6; }
.memory-card {
  padding: 1rem 1.1rem;
  margin-bottom: 0.9rem;
  border: 1px solid var(--border-color, #e6eaf0);
  border-radius: 12px;
  background: var(--card-bg, rgba(255,255,255,0.6));
}
.memory-card-heading { align-items: center; margin-bottom: 0.9rem; }
.memory-card-heading h5, .memory-clear-row strong { margin: 0; color: var(--text-primary, #2d3748); font-size: 1rem; }
.memory-content-preview, .memory-item-content, .memory-empty-state {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin: 0;
  color: var(--text-primary, #344054);
  font-size: 0.9rem;
  line-height: 1.65;
}
.memory-empty-state { padding: 0.85rem; border-radius: 8px; background: rgba(127, 140, 160, 0.07); color: var(--text-secondary, #718096); }
.memory-textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 5rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #d9e0e8);
  border-radius: 8px;
  background: var(--input-bg, #fff);
  color: var(--text-primary, #2d3748);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.55;
  resize: vertical;
}
.memory-textarea:focus { outline: 2px solid rgba(69, 130, 255, 0.3); border-color: #548bff; }
.memory-action { width: auto; min-width: 5rem; padding: 0.5rem 0.85rem; }
.memory-editor-actions, .memory-item-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.75rem; }
.memory-item-list { display: grid; gap: 0.65rem; }
.memory-item { padding: 0.85rem; border-radius: 9px; background: rgba(127, 140, 160, 0.07); }
.memory-item-label { display: block; margin-bottom: 0.25rem; color: var(--text-secondary, #718096); font-size: 0.76rem; }
.memory-text-button { padding: 0.2rem 0.35rem; border: 0; background: transparent; color: #4774bb; cursor: pointer; font: inherit; font-size: 0.82rem; }
.memory-text-button.danger { color: #c34444; }
.memory-add-button { margin-top: 0.8rem; padding: 0.55rem 0; border: 0; background: transparent; color: #4774bb; cursor: pointer; font: inherit; font-weight: 600; }
.memory-add-editor { margin-top: 0.75rem; }
.memory-clear-row { align-items: center; padding: 1rem; border: 1px solid rgba(204, 68, 68, 0.2); border-radius: 12px; background: rgba(204, 68, 68, 0.045); }
.memory-clear-row p { max-width: 34rem; }
@media (max-width: 600px) {
  .memory-page-heading, .memory-clear-row { align-items: stretch; flex-direction: column; }
  .memory-page-heading { flex-direction: row; }
  .memory-local-badge { align-self: flex-start; }
  .memory-clear-row .memory-action { width: 100%; }
  .memory-card { padding: 0.85rem; }
}

.model-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-secondary {
  background: var(--bg-card);
  color: var(--primary-blue);
  border: 1px solid var(--primary-blue);
}

.model-balance {
  margin-top: 4px;
  color: var(--primary-blue);
  font-size: 12px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-family: inherit;
  background: var(--bg-card);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-blue);
  background: var(--bg-main);
}

/* Modern Model Dashboard Styles */
.modern-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: var(--primary-blue);
  color: #fff;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-main-title {
  font-weight: 700;
  margin: 0 0 0.2rem 0;
  color: var(--text-main);
  letter-spacing: -0.5px;
}

.panel-subtitle {
  color: var(--text-secondary);
  margin: 0;
}

.add-model-btn-top {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.2rem;
  min-width: auto;
}

.model-dashboard {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.model-category-card {
  background: var(--bg-card);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.category-title-area {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.category-icon {
}

.category-title-area h5 {
  margin: 0;
  font-weight: 700;
  color: var(--text-main);
}

.model-count {
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--soft-blue);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
}

.empty-state-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
  background: var(--soft-blue);
  border-radius: var(--radius-lg);
  border: 1px dashed rgba(47, 84, 235, 0.2);
}

.empty-icon-wrapper {
  width: 48px;
  height: 48px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--text-secondary);
  box-shadow: var(--shadow-sm);
}

[data-theme="dark"] .empty-icon-wrapper {
  background: var(--bg-main);
}

.empty-state-modern p {
  margin: 0;
  font-weight: 500;
}

.agent-mode-icon {
  color: var(--primary-blue);
  background: #fff;
}

.agent-mode-title {
  font-weight: 700 !important;
  color: var(--text-main);
  margin-bottom: 0.5rem !important;
}

.agent-mode-desc {
  color: var(--text-secondary);
  line-height: 1.6;
  text-align: center;
}

.mt-3 {
  margin-top: 1.5rem;
}

.modern-model-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.modern-model-card {
  position: relative;
  background: var(--bg-main);
  border-radius: var(--radius-lg);
  padding: 1.2rem;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modern-model-card:hover {
  border-color: var(--primary-blue);
  background: var(--soft-blue);
}

.modern-model-card.is-active {
  border-color: var(--primary-blue);
  background: var(--soft-blue);
}

.card-status-indicator {
  position: absolute;
  top: 1.2rem;
  left: 0;
  width: 4px;
  height: 24px;
  background: var(--primary-blue);
  border-radius: 0 4px 4px 0;
  opacity: 0;
  transition: opacity 0.3s;
}

.modern-model-card.is-active .card-status-indicator {
  opacity: 1;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-left: 0.5rem;
}

.model-title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 70%;
}

.model-title {
  margin: 0;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-badge {
  font-weight: 700;
  color: var(--primary-blue);
  background: #fff;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  display: inline-block;
  width: fit-content;
  border: 1px solid var(--primary-blue);
}

[data-theme="dark"] .active-badge {
  background: var(--bg-card);
}

.card-actions {
  display: flex;
  gap: 0.4rem;
}

.action-btn {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: var(--text-main);
  color: var(--bg-main);
  border-color: var(--text-main);
}

.action-btn.delete:hover {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}

.user-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--bg-card);
  padding: 12px 16px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color);
}

.status-info {
  display: flex;
  flex-direction: column;
}

.status-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-main);
}

.status-email {
  font-size: 12px;
  color: var(--text-secondary);
}

.logout-btn {
  color: #ef4444;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  background: rgba(239, 68, 68, 0.1);
  font-weight: 600;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: #ef4444;
  color: #fff;
}

.card-bottom {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-left: 0.5rem;
}

.model-detail-pill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-secondary);
  font-family: monospace;
}

.model-endpoint-text {
  color: var(--text-secondary);
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Modern Model Editor */
.model-editor-modern {
  background: var(--bg-card);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.editor-header-modern {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.back-btn-modern {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--soft-blue);
  color: var(--primary-blue);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-pill);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn-modern:hover {
  background: var(--primary-blue);
  color: #fff;
  transform: translateX(-4px);
}

.editor-header-modern h5 {
  margin: 0;
  font-weight: 700;
}

.editor-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.form-col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group-modern label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-weight: 600;
  color: var(--text-main);
}

.course-link {
  color: var(--primary-blue);
  text-decoration: none;
  background: var(--soft-blue);
  padding: 2px 8px;
  border-radius: var(--radius-pill);
}

.course-link:hover {
  text-decoration: underline;
}

.help-text {
  color: var(--text-secondary);
  font-weight: normal;
}

.modern-input {
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-family: inherit;
  background: var(--bg-main);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.modern-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  background: var(--bg-card);
}

.select-wrapper {
  position: relative;
}

.select-wrapper select {
  appearance: none;
  padding-right: 2.5rem;
}

.select-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary);
}

.password-input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
}

.type-selector {
  display: flex;
  background: var(--bg-main);
  padding: 4px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
}

.type-option {
  flex: 1;
  text-align: center;
  padding: 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.type-option.active {
  background: var(--bg-card);
  color: var(--primary-blue);
  box-shadow: var(--shadow-sm);
}

.btn-block {
  width: 100%;
  padding: 1rem;
}

/* Cloud Sync Section */
.cloud-backup {
  background: linear-gradient(135deg, var(--soft-blue) 0%, rgba(47, 84, 235, 0.02) 100%);
  border: 1px solid rgba(47, 84, 235, 0.1);
}

.user-status {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
  font-weight: 600;
}

.login-prompt {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ccc;
}

.status-dot.online {
  background: #22c55e;
}

.btn-group {
  display: flex;
  gap: 1rem;
}

.btn-text-small {
  background: none;
  border: none;
  color: #ef4444;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  margin-left: auto;
  border-radius: var(--radius-md);
  transition: background 0.2s;
}

.btn-text-small:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* Backup Actions */
.backup-section {
  padding: 1.5rem;
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  margin-bottom: 1.2rem;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s;
}

.backup-section:hover {
  background: var(--bg-main);
}

.backup-section h5 {
  margin: 0 0 0.6rem 0;
  font-weight: 700;
}

.backup-section p {
  color: var(--text-secondary);
  margin-bottom: 1.2rem;
  line-height: 1.5;
}

.danger-zone {
  background: rgba(239, 68, 68, 0.02);
  border: 1px solid rgba(239, 68, 68, 0.1);
}

[data-theme="dark"] .danger-zone {
  background: rgba(239, 68, 68, 0.05);
}

.btn-primary, .btn-secondary, .btn-danger {
  padding: 0.8rem 1.8rem;
  border-radius: var(--radius-pill);
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  min-width: 130px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: var(--primary-blue);
  color: #fff;
  border: 1px solid var(--primary-blue);
}

.btn-primary:hover {
  background: var(--primary-blue-hover);
  border-color: var(--primary-blue-hover);
}

.btn-secondary {
  background: var(--bg-card);
  color: var(--text-main);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover {
  background: var(--bg-main);
}

.btn-danger {
  background: #ef4444;
  color: #fff;
  border: 1px solid #ef4444;
}

.btn-danger:hover {
  background: #dc2626;
  border-color: #dc2626;
}

.modal-footer {
  padding-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  position: sticky;
  bottom: -2.5rem;
  background: linear-gradient(to top, var(--bg-main) 50%, transparent);
  padding-bottom: 2.5rem;
  z-index: 10;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .modal-overlay {
    animation: fadeIn 0.2s ease-out;
  }

/* About Section */
.about-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
}

.about-desc {
  line-height: 1.7;
  color: var(--text-main);
  margin: 0;
  text-align: center;
}

.link-card {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}

.github-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background-color: var(--bg-card);
  color: var(--text-main);
  border-radius: var(--radius-pill);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.github-link:hover {
  background: var(--bg-main);
  color: var(--primary-blue);
}

.version-info {
  text-align: center;
  margin-top: 1rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.legal-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.85rem;
  transition: color 0.2s;
}

.legal-link:hover {
  color: var(--primary-blue);
}

.legal-divider {
  color: var(--border-color);
}

.version-info p {
  margin-bottom: 0.8rem;
}

.version-text {
  font-weight: 600;
  background: var(--bg-card);
  padding: 4px 12px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-color);
}

/* Appearance Settings Styles */
.appearance-card {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  padding: 2rem;
  margin-top: 1rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
}

.appearance-card:hover {
  background: var(--bg-main);
}

.appearance-header {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  margin-bottom: 2rem;
}

.appearance-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.appearance-label {
  font-weight: 700;
  color: var(--text-main);
}

.appearance-value {
  background: var(--primary-blue);
  color: white;
  padding: 4px 12px;
  border-radius: var(--radius-pill);
  font-weight: 600;
}

.slider-wrapper {
  padding: 10px 0;
}

.modern-slider {
  -webkit-appearance: none;
  width: 100%;
  height: 8px;
  background: var(--soft-blue);
  border-radius: var(--radius-pill);
  outline: none;
  transition: background 0.2s;
}

[data-theme="dark"] .modern-slider {
  background: rgba(255,255,255,0.1);
}

.modern-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  background: var(--primary-blue);
  border: 4px solid white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.modern-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.modern-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2.5rem;
  background: var(--soft-blue);
  border-radius: var(--radius-md);
}

.preview-bubble {
  padding: 1rem 1.5rem;
  background: var(--primary-blue);
  color: white;
  border-radius: 24px 24px 24px 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 90%;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.bubble-icon {
}

/* Backup & Sync Refactor Styles */
.tab-content { display: flex; flex-direction: column; }
.space-y-6 > * + * { margin-top: 1.5rem; }
.pb-6 { padding-bottom: 1.5rem; }
.bg-card { background: var(--bg-card); }
.rounded-\[24px\] { border-radius: var(--radius-xl); }
.shadow-card { border: 1px solid var(--border-color); }
.p-5 { padding: 1.25rem; }
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.mb-4 { margin-bottom: 1rem; }
.space-x-4 > * + * { margin-left: 1rem; }
.space-x-3 > * + * { margin-left: 0.75rem; }
.space-x-2 > * + * { margin-left: 0.5rem; }
.w-12 { width: 3rem; }
.h-12 { height: 3rem; }
.rounded-full { border-radius: 9999px; }
.bg-gray-100 { background-color: #f3f4f6; }
.text-gray-400 { color: #9ca3af; }
.text-gray-600 { color: #4b5563; }
.text-gray-300 { color: #d1d5db; }
.text-gray-200 { color: #e5e7eb; }
.text-\[16px\] { font-size: 16px; }
.text-\[15px\] { font-size: 15px; }
.text-\[13px\] { font-size: 13px; }
.text-\[12px\] { font-size: 12px; }
.text-xs { font-size: 0.75rem; }
.text-sm { font-size: 0.875rem; }
.font-semibold { font-weight: 600; }
.font-medium { font-weight: 500; }
.text-textSub { color: var(--text-secondary); }
.mt-0.5 { margin-top: 0.125rem; }
.px-5 { padding-left: 1.25rem; padding-right: 1.25rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.bg-primary { background-color: var(--primary-blue); }
.text-white { color: #ffffff; }
.active\:scale-95:active { transform: scale(0.95); }
.transition-transform { transition: transform 0.2s; }
.shadow-md { border: 1px solid var(--border-color); }
.shadow-primary\/30 { border: 1px solid var(--primary-blue); }
.overflow-hidden { overflow: hidden; }
.border-b { border-bottom: 1px solid var(--border-color); }
.border-gray-100 { border-color: var(--border-color); }
.cursor-pointer { cursor: pointer; }
.hover\:bg-gray-50\/50:hover { background-color: rgba(249, 250, 251, 0.5); }
.transition-colors { transition: background-color 0.2s; }
.bg-blue-50 { background-color: var(--soft-blue); }
.text-primary { color: var(--primary-blue); }
.w-8 { width: 2rem; }
.h-8 { height: 2rem; }
.rounded-xl { border-radius: 0.75rem; }
.bg-yellow-50 { background-color: #fffbeb; }
.text-yellow-600 { color: #d97706; }
.bg-green-50 { background-color: #f0fdf4; }
.text-green-600 { color: #16a34a; }
.bg-purple-50 { background-color: #faf5ff; }
.text-purple-600 { color: #9333ea; }
.bg-red-50\/50 { background-color: rgba(254, 242, 242, 0.5); }
.border-red-100 { border-color: #fee2e2; }
.text-red-500 { color: #ef4444; }
.text-red-600 { color: #dc2626; }
.text-red-400 { color: #f87171; }
.text-red-200 { color: #fecaca; }
.bg-red-100 { background-color: #fee2e2; }
.active\:bg-red-50:active { background-color: #fef2f2; }
.bg-transparent { background-color: transparent; }
.border-none { border: none; }
.text-left { text-align: left; }
.flex-col { flex-direction: column; }

.local-data-section-header {
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.local-data-action-btn {
  color: var(--text-main);
}

.local-data-action-btn:hover {
  background-color: rgba(15, 23, 42, 0.03);
}

.local-data-action-btn:active {
  background-color: rgba(15, 23, 42, 0.06);
}

.local-data-action-arrow {
  transition: color 0.2s ease;
}

[data-theme="dark"] .local-data-section-header {
  background-color: rgba(255, 255, 255, 0.04);
}

[data-theme="dark"] .local-data-action-btn:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

[data-theme="dark"] .local-data-action-btn:active {
  background-color: rgba(255, 255, 255, 0.08);
}

[data-theme="dark"] .local-data-action-arrow {
  color: #64748b;
}

[data-theme="dark"] .local-data-action-btn:hover .local-data-action-arrow,
[data-theme="dark"] .local-data-action-btn:active .local-data-action-arrow {
  color: #94a3b8;
}

[data-theme="dark"] .bg-gray-100 { background-color: rgba(255,255,255,0.05); }
[data-theme="dark"] .bg-blue-50 { background-color: rgba(79, 113, 255, 0.1); }
[data-theme="dark"] .bg-yellow-50 { background-color: rgba(217, 119, 6, 0.1); }
[data-theme="dark"] .bg-green-50 { background-color: rgba(22, 163, 74, 0.1); }
[data-theme="dark"] .bg-purple-50 { background-color: rgba(147, 51, 234, 0.1); }
[data-theme="dark"] .bg-red-50\/50 { background-color: rgba(239, 68, 68, 0.05); }
[data-theme="dark"] .bg-red-100 { background-color: rgba(239, 68, 68, 0.15); }

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.m-0 { margin: 0; }

/* Toggle Styles */
.toggle-checkbox:checked {
  right: 0;
  border-color: var(--primary-blue);
}
.toggle-checkbox:checked + .toggle-label {
  background-color: var(--primary-blue);
}
.toggle-checkbox {
  right: 24px;
  top: 0;
  transition: all 0.3s;
  border-color: #d1d5db;
}
.toggle-label {
  height: 24px;
}

/* Compact Model Panel Styles */
.compact-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
.panel-main-title {
  white-space: nowrap;
}
.add-btn-pill { 
  flex: 0 0 auto; 
  width: max-content !important; 
  min-width: 0 !important;
  border-radius: var(--radius-pill); 
  padding: 0.5rem 1.2rem; 
  font-weight: 600;
  border: none;
}
.btn-small { padding: 0.4rem 0.8rem; min-width: auto; }
.compact-header .btn-primary,
.compact-header .btn-secondary,
.compact-editor .btn-primary {
  border: none;
}
.compact-header .btn-secondary {
  background: rgba(47, 84, 235, 0.1);
  color: var(--primary-blue);
}
.action-div-btn { display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; cursor: pointer; user-select: none; }
.compact-dashboard { display: flex; flex-direction: column; gap: 0.85rem; }
.model-section {
  background: var(--bg-card);
  border-radius: 18px;
  overflow: visible;
}
.model-section-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border: none;
  background: transparent;
  color: var(--text-main);
  cursor: pointer;
  text-align: left;
}
.model-section-title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.8rem;
}
.model-section-icon {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 12px;
  background: var(--bg-main);
}
.model-section-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.18rem;
}
.model-section-copy strong {
  color: var(--text-main);
  font-size: 0.98rem;
  line-height: 1.25;
}
.model-section-copy small {
  max-width: 280px;
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.model-section-meta {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0.55rem;
}
.model-count-pill {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--bg-main);
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 700;
}
.model-section.is-collapsed .collapse-chevron {
  transform: rotate(-90deg);
}
.model-section-body {
  padding: 0 0.75rem 0.75rem;
}
.simple-empty-state {
  padding: 1.4rem;
  border-radius: 14px;
  background: var(--bg-main);
  color: var(--text-secondary);
  text-align: center;
}
.simple-model-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.simple-model-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.85rem;
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  transition: background 0.18s ease;
}
.simple-model-drag-handle,
.model-capsule-drag-handle {
  flex: 0 0 auto;
  color: var(--text-secondary);
  cursor: grab;
  user-select: none;
}
.simple-model-drag-handle,
.model-capsule-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  line-height: 0;
}
.simple-model-drag-handle svg,
.model-capsule-drag-handle svg {
  display: block;
}
.simple-model-row:active,
.model-capsule:active { cursor: grabbing; }
.simple-model-row:hover {
  background: var(--bg-main);
}
.simple-model-row.is-drag-over { outline: 2px dashed var(--primary-blue); outline-offset: -2px; background: rgba(47, 84, 235, .08); }
.simple-model-row.is-active {
  position: relative;
  background: linear-gradient(105deg, rgba(47, 84, 235, 0.16), rgba(124, 92, 255, 0.08));
  box-shadow: inset 0 0 0 1px rgba(47, 84, 235, 0.24), 0 5px 16px rgba(47, 84, 235, 0.09);
}
.simple-model-row.is-active::before {
  position: absolute;
  top: 0.55rem;
  bottom: 0.55rem;
  left: 0;
  width: 4px;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #2f54eb, #7c3aed);
  content: '';
}
.simple-model-row.is-active .simple-model-title > span:first-child {
  color: #2346c7;
}
.simple-model-row.is-active .simple-active-label {
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: rgba(47, 84, 235, 0.12);
  color: #2f54eb;
}
[data-theme="dark"] .simple-model-row.is-active {
  background: linear-gradient(105deg, rgba(47, 84, 235, 0.3), rgba(124, 92, 255, 0.18));
  box-shadow: inset 0 0 0 1px rgba(129, 140, 248, 0.38), 0 5px 18px rgba(30, 64, 175, 0.2);
}
[data-theme="dark"] .simple-model-row.is-active .simple-model-title > span:first-child {
  color: #e0e7ff;
}
[data-theme="dark"] .simple-model-row.is-active .simple-active-label {
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: rgba(165, 180, 252, 0.18);
  color: #c7d2fe;
}
.simple-model-main {
  min-width: 0;
  flex: 1;
}
.simple-model-title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.5rem;
}
.simple-model-title > span:first-child {
  overflow: hidden;
  color: var(--text-main);
  font-size: 0.92rem;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.simple-active-label {
  flex: 0 0 auto;
  color: var(--primary-blue);
  font-size: 0.72rem;
  font-weight: 800;
}
.simple-model-subtitle {
  margin-top: 0.2rem;
  overflow: hidden;
  color: var(--text-secondary);
  font-family: monospace;
  font-size: 0.76rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.simple-model-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0.35rem;
}
.simple-action-btn {
  padding: 0.35rem 0.55rem;
  border: none;
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.12);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 700;
}
.simple-action-btn:hover {
  background: rgba(47, 84, 235, 0.12);
  color: var(--primary-blue);
}
.simple-action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
.simple-model-more {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.simple-more-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 0;
}
.simple-more-btn:hover,
.simple-more-btn[aria-expanded="true"] {
  border-color: rgba(47, 84, 235, .18);
  background: rgba(47, 84, 235, .1);
  color: var(--primary-blue);
}
.simple-model-menu {
  position: absolute;
  z-index: 20;
  top: calc(100% + 0.35rem);
  right: 0;
  min-width: 142px;
  padding: 0.3rem;
  border: 1px solid rgba(100, 116, 139, .18);
  border-radius: 12px;
  background: var(--bg-card);
  box-shadow: 0 12px 30px rgba(15, 23, 42, .16);
}
.simple-model-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.65rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-main);
  cursor: pointer;
  font-size: 0.78rem;
  text-align: left;
  white-space: nowrap;
}
.simple-model-menu-item:hover {
  background: var(--bg-main);
  color: var(--primary-blue);
}
.simple-model-menu-item.danger:hover {
  color: #ef4444;
}
.simple-model-menu-item:disabled {
  cursor: not-allowed;
  opacity: .45;
}
.compact-category { 
  position: relative;
  background: var(--bg-card); 
  border-radius: 18px; 
  border: none; 
  overflow: hidden; 
  box-shadow: none;
  transition: background 0.2s ease, transform 0.2s ease;
}
.compact-category:hover {
  background: rgba(255, 255, 255, 0.82);
}
[data-theme="dark"] .compact-category {
  background: rgba(255, 255, 255, 0.045);
  box-shadow: none;
}
[data-theme="dark"] .compact-category:hover {
  background: rgba(255, 255, 255, 0.065);
}
.category-header-compact { 
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem; 
  background: transparent; 
  border: none;
  color: var(--text-main);
  cursor: pointer;
  text-align: left;
}
.category-title-wrap {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}
.category-icon-badge {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 13px;
  background: rgba(47, 84, 235, 0.09);
  box-shadow: none;
}
.category-title-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.15rem;
}
.category-title-text strong {
  color: var(--text-main);
  font-size: 0.98rem;
  line-height: 1.25;
}
.category-title-text small {
  color: var(--text-secondary);
  font-size: 0.76rem;
}
.category-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  gap: 0.6rem;
}
.active-model-chip {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.32rem 0.7rem;
  border-radius: var(--radius-pill);
  background: rgba(47, 84, 235, 0.1);
  color: var(--primary-blue);
  font-size: 0.76rem;
  font-weight: 700;
}
.category-count {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(100, 116, 139, 0.1);
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 700;
}
.collapse-chevron {
  color: var(--text-secondary);
  transition: transform 0.2s ease;
}
.compact-category.is-collapsed .collapse-chevron {
  transform: rotate(-90deg);
}
.category-content {
  padding: 0 1rem 1rem;
}
.empty-state-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1.6rem 1.25rem;
  text-align: center;
  color: var(--text-secondary);
  border-radius: 14px;
  border: none;
  background: var(--bg-main);
}
.empty-icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(47, 84, 235, 0.1);
  color: var(--primary-blue);
  font-weight: 800;
}
.text-sm { }
.compact-model-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.75rem;
}
.compact-model-item { 
  display: flex; 
  justify-content: space-between; 
  align-items: flex-start; 
  gap: 0.85rem;
  min-width: 0;
  padding: 1rem; 
  border: none;
  border-radius: 14px;
  transition: background 0.2s ease, transform 0.2s ease; 
  cursor: pointer; 
  background: var(--bg-main);
}
.compact-model-item:hover {
  background: rgba(47, 84, 235, 0.07);
  transform: translateY(-1px);
  box-shadow: none;
}
.compact-model-item.is-active { 
  background: rgba(47, 84, 235, 0.12); 
  box-shadow: none;
}
.model-info {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}
.model-title-row {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.5rem;
}
.model-name { 
  font-weight: 600; 
  font-size: 0.92rem;
  color: var(--text-main); 
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-badge {
  flex: 0 0 auto;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-pill);
  background: rgba(47, 84, 235, 0.12);
  color: var(--primary-blue);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.model-tag-wrapper {
  display: flex;
  align-items: center;
  min-width: 0;
}
.active-dot { background: var(--primary-blue); border-radius: 50%; }
.model-id { 
  font-family: monospace; 
  color: var(--text-secondary); 
  background: rgba(100, 116, 139, 0.1); 
  padding: 5px 8px; 
  border-radius: 8px; 
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.78rem;
}
.model-actions { display: flex; gap: 0.35rem; opacity: 0; transition: opacity 0.2s; }
.compact-model-item:hover .model-actions,
.compact-model-item.is-active .model-actions { opacity: 1; }
.icon-btn { background: rgba(100, 116, 139, 0.1); border: none; color: var(--text-secondary); cursor: pointer; padding: 6px; border-radius: 8px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.icon-btn:hover { background: rgba(47, 84, 235, 0.12); color: var(--primary-blue); }
.icon-btn.delete:hover { background: rgba(239, 68, 68, 0.12); color: #ef4444; }

/* Compact Editor */
.compact-editor { background: var(--bg-card); border-radius: 18px; padding: 1.5rem; border: none; }
.editor-header-compact { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 0; border-bottom: none; }
.editor-header-compact h5 { margin: 0; font-weight: 600; }
.compact-form { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; }
.form-row { display: flex; gap: 1rem; }
.form-row > * { flex: 1; }
.model-name-row { display: block; }
.model-config-name-field { width: calc(50% - .5rem); }
.model-identifier-field { width: 100%; }
.form-group-compact { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group-compact label { font-weight: 600; color: var(--text-secondary); display: flex; justify-content: space-between; align-items: center; margin: 0;}
.compact-input { width: 100%; padding: 0.6rem 0.8rem; border: none; border-radius: 10px; background: var(--bg-main); color: var(--text-main); transition: all 0.2s; box-sizing: border-box; }
.compact-input:focus { outline: none; border-color: var(--primary-blue); background: var(--bg-card); }
.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
}
.inline-fetch-action {
  color: var(--primary-blue);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease, color 0.2s ease;
}
.inline-fetch-action:hover {
  opacity: 0.8;
}
.inline-fetch-action.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}
.custom-model-combobox {
  position: relative;
}
.custom-combobox-input {
  position: relative;
}
.custom-combobox-input.open .custom-combobox-field {
  background: var(--bg-card);
  box-shadow: inset 0 0 0 1px rgba(47, 84, 235, 0.22);
}
.custom-combobox-input.disabled .custom-combobox-field {
  opacity: 0.78;
}
.custom-combobox-field {
  padding-right: 2.75rem;
}
.simple-action-btn.pin-action.pinned,
.model-capsule-pin.pinned { color: #f59e0b; }
.model-picker {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: .45rem;
}
.model-picker-toolbar {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: .5rem;
}
.model-custom-input-wrap { position: relative; min-width: 0; flex: 1; }
.model-picker-search { min-width: 0; flex: 1; padding-right: 2.5rem; }
.model-custom-add { position: absolute; top: 50%; right: .35rem; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; transform: translateY(-50%); border: 0; border-radius: 8px; background: var(--primary-blue); color: #fff; font-size: 1.25rem; line-height: 1; cursor: pointer; }
.model-custom-add:hover { filter: brightness(1.08); }
.standalone-custom-model-input { grid-column: 1 / -1; width: 100%; }
.standalone-custom-model-input .custom-combobox-field { padding-right: 2.5rem; }
.model-picker-select-all {
  flex: 0 0 auto;
  border: 0;
  border-radius: 9px;
  padding: .55rem .7rem;
  background: rgba(47, 84, 235, .08);
  color: var(--primary-blue);
  font-size: .75rem;
  font-weight: 700;
  cursor: pointer;
}
.model-picker-summary { grid-column: 1 / -1; color: var(--text-secondary); font-size: .75rem; }
.model-picker-section { display: flex; flex-direction: column; gap: .4rem; }
.model-picker-section-title { display: flex; align-items: center; justify-content: space-between; color: var(--text-secondary); font-size: .78rem; font-weight: 700; }
.model-picker-section-title small { min-width: 20px; padding: 2px 6px; border-radius: 999px; background: rgba(47, 84, 235, .1); color: var(--primary-blue); font-size: .68rem; text-align: center; }
.model-capsule-cloud { display: flex; flex-wrap: wrap; align-items: flex-start; align-content: flex-start; gap: .45rem; max-height: 178px; overflow-y: auto; padding: .55rem; border: 1px solid rgba(100, 116, 139, .14); border-radius: 12px; background: var(--bg-main); }
.model-capsule { max-width: 100%; display: inline-flex; align-items: center; gap: .35rem; min-height: 30px; padding: .35rem .6rem; border: 1px solid rgba(100, 116, 139, .2); border-radius: 999px; background: var(--bg-card); color: var(--text-main); cursor: pointer; font-size: .74rem; line-height: 1.2; transition: border-color .16s ease, background .16s ease, color .16s ease, transform .16s ease; }
.model-capsule-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.model-capsule-pin,
.model-capsule-remove { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; padding: 0; border: 0; background: transparent; color: inherit; cursor: pointer; font-size: .9rem; line-height: 1; }
.model-capsule-remove { opacity: .7; }
.model-capsule-remove:hover { opacity: 1; }
.model-capsule:hover { border-color: var(--primary-blue); transform: translateY(-1px); }
.model-capsule.selected { border-color: rgba(47, 84, 235, .45); background: rgba(47, 84, 235, .1); color: var(--primary-blue); }
.model-capsule.selected.pinned { border-color: rgba(245, 158, 11, .65); background: rgba(245, 158, 11, .1); }
.model-capsule.is-drag-over { outline: 2px dashed var(--primary-blue); outline-offset: 1px; }
.selected-models-section { margin-top: .15rem; }
.selected-model-capsule-cloud { max-height: 105px; background: rgba(47, 84, 235, .035); }
.model-picker-empty-selected { padding: .7rem; border: 1px dashed rgba(100, 116, 139, .25); border-radius: 10px; color: var(--text-secondary); font-size: .75rem; text-align: center; }
@media (max-width: 720px) {
  .model-picker { grid-template-columns: minmax(0, 1fr); }
  .model-picker-toolbar, .model-picker-summary { grid-column: auto; }
  .model-config-name-field { width: 100%; }
}
.model-picker-list {
  max-height: 220px;
  overflow-y: auto;
  padding: .3rem;
  border: 1px solid rgba(100, 116, 139, .14);
  border-radius: 12px;
  background: var(--bg-main);
}
.model-picker-option {
  display: flex;
  align-items: center;
  gap: .55rem;
  min-height: 38px;
  padding: .35rem .45rem;
  border-radius: 9px;
  cursor: pointer;
}
.model-picker-option:hover, .model-picker-option.selected { background: rgba(47, 84, 235, .08); }
.model-picker-option > input { position: absolute; opacity: 0; pointer-events: none; }
.model-picker-check {
  width: 18px; height: 18px; flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(100, 116, 139, .35); border-radius: 5px;
  color: transparent; background: var(--bg-card);
}
.model-picker-option.selected .model-picker-check { border-color: var(--primary-blue); background: var(--primary-blue); color: #fff; }
.model-picker-option-copy { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.model-picker-option-copy strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .82rem; color: var(--text-main); }
.model-picker-option-copy small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-secondary); font-size: .7rem; }
.model-picker-active-row { display: flex; align-items: center; gap: .6rem; }
.model-picker-active-row label { flex: 0 0 auto; color: var(--text-secondary); font-size: .78rem; font-weight: 600; }
.model-picker-active-row select { min-width: 0; flex: 1; }
.custom-combobox-toggle {
  position: absolute;
  top: 50%;
  right: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: pointer;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}
.custom-combobox-input.open .custom-combobox-toggle {
  color: var(--primary-blue);
  transform: translateY(-50%) rotate(180deg);
}
.custom-combobox-toggle.disabled {
  opacity: 0.4;
  cursor: default;
}
.custom-combobox-panel {
  position: absolute;
  top: calc(100% + 0.45rem);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  padding: 0.4rem;
  border-radius: 14px;
  background: var(--bg-card);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(100, 116, 139, 0.12);
}
.custom-combobox-option {
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}
.custom-combobox-option:hover,
.custom-combobox-option.highlighted {
  background: rgba(47, 84, 235, 0.08);
}
.custom-combobox-option.active {
  background: rgba(47, 84, 235, 0.12);
}
.custom-combobox-option-title {
  color: var(--text-main);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.35;
}
.custom-combobox-option-meta {
  margin-top: 0.15rem;
  color: var(--text-secondary);
  font-size: 0.74rem;
  line-height: 1.4;
}
.custom-combobox-empty {
  padding: 0.85rem 0.8rem;
  color: var(--text-secondary);
  font-size: 0.78rem;
  text-align: center;
}
.model-fetch-feedback {
  margin-top: 0.15rem;
  color: var(--text-secondary);
  font-size: 0.76rem;
  line-height: 1.45;
}
.model-fetch-feedback.error {
  color: #ef4444;
}
.type-selector-compact {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  width: 100%;
  background: var(--bg-main);
  padding: 3px;
  border-radius: 6px;
  border: none;
}
.type-option-compact {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;
  padding:6px 3px;
  border-radius: 4px;
  border: 1px solid transparent;
  color: var(--text-secondary);
  font-size:small;
  font-weight: 600;
  transition: all 0.2s;
}
.type-option-compact.active {
  background: var(--bg-card);
  color: var(--primary-blue);
  border-color: var(--primary-blue);
}
.editor-footer-compact { display: flex; justify-content: flex-end; }

/* Mobile Optimization */
@media (max-width: 768px) {
  .modal-overlay, [data-theme="dark"] .modal-overlay {
    background-color: var(--bg-main);
    backdrop-filter: none;
    padding: 0;
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .modal-container {
    
    flex-direction: column;
    width: 100%;
    height: calc(100vh);
    max-height: calc(100vh );
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  .modal-sidebar {
    width: 100%;
    padding: 1rem 1.2rem 0.5rem; /* 更加紧凑的内边距 */
    border-right: none;
    border-bottom: 1px solid var(--border-color);
    z-index: 10;
  }

  .modal-sidebar.agent-mode {
    padding-top: 70px; /* 避开 BrowserAgentLayout 的 header (约44-50px) */
  }

  .modal-header {
    padding-bottom: 0.8rem;
  }

  .settings-nav {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    gap: 0.5rem;
    scrollbar-width: none;
  }

  .settings-nav::-webkit-scrollbar {
    display: none;
  }

  .settings-nav li {
    padding: 6px 12px; /* 减小标签尺寸 */
    font-size: 14px;
    white-space: nowrap;
  }

  .settings-nav li:hover {
    transform: none;
  }

  .modal-content {
    padding: 1.2rem 1.2rem calc(1.2rem + env(safe-area-inset-bottom, 0px));
    position: static;
  }

  .settings-panel h4, .panel-main-title {
    margin-bottom: 0.5rem;
  }

  .modern-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .editor-form-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .add-model-container {
    margin-top: 0;
    margin-bottom: 1.5rem;
    justify-content: flex-start;
  }

  .model-list-container {
    gap: 1.8rem;
  }

  .empty-hint {
    padding: 3rem 1.5rem;
    border-radius: var(--radius-lg);
  }

  .compact-model-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.8rem;
    padding: 1rem;
  }

  .model-info {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }

  .model-name {
    width: 100%;
    white-space: normal;
    padding-right: 4rem; /* 为操作按钮留出空间 */
  }

  .modal-close-btn {
    padding: 0.4rem;
    background: transparent;
    border-color: transparent;
    opacity: 0.8;
  }

  .category-header-compact {
    align-items: flex-start;
  }

  .category-meta {
    flex-shrink: 0;
  }

  .active-model-chip {
    display: none;
  }

  .model-section-header {
    align-items: flex-start;
  }

  .model-section-copy small {
    max-width: 180px;
  }

  .simple-model-row {
    align-items: center;
  }

  .simple-model-actions {
    flex-direction: row;
    align-items: center;
  }

  .compact-model-item .model-name {
    padding-right: 0 !important;
  }

  .field-label-row {
    align-items: center;
  }

  .model-actions {
    position: static;
    width: auto;
    justify-content: flex-end;
    border-top: none;
    padding-top: 0;
    opacity: 1; /* 移动端常驻显示 */
  }

  .appearance-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 0.8rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    bottom: calc(-1rem - env(safe-area-inset-bottom, 0px));
  }

  .btn-primary, .btn-secondary, .btn-danger {
    width: 100%;
  }

  .github-link {
    padding: 0.8rem 1.2rem;
  }
}
</style>
